import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  CircleMarker,
  useMapEvents,
  Polygon,
  Polyline,
  Tooltip as LeafletTooltip,
} from "react-leaflet";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import {
  Grid,
  Typography,
  Button,
  IconButton,
  Paper,
  Modal,
  Menu,
  MenuItem,
  Tooltip,
  Avatar,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import "leaflet/dist/leaflet.css";
import "./decade_map.css";
import "./web19201.css";
import ContributionComp from "./ContributionComp";
import baseLayerOptions from "./baseLayerOptions";
import { useUserPreferences } from "./UserPreferencesContext";
import Preferences from "./Preferences";
import ErrorBoundary from "./ErrorBoundary";
import AuthForm from "./authForm";
import { useAuth } from "./authContext";
import SidePanelComponent from "./SidePanelComponent";
import { useProject } from "./ProjectContext";
import { useFilter, SHOW_MY_PLACES_MODE } from "./FilterContext";
import ContributorsDirectory from "./ContributorsDirectory";
import EntryTypeToggle from "./EntryTypeToggle";
import FilterBar from "./FilterBar";
import { useNavigate } from "react-router-dom";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import "leaflet-draw/dist/leaflet.draw.css";
import L from "leaflet";
import { NavigateBefore, NavigateNext } from "@mui/icons-material";
import "./MapComponent.css";
import SearchBar from "./SearchBar";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import InfoIcon from "@mui/icons-material/Info";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ReactDOM from "react-dom";
import { renderToString } from "react-dom/server";
import config from "./config";
import { apiCall } from "./api";
import AddressSuggestionDialog from "./AddressSuggestionDialog";
import LocationPostsTable from "./LocationPostsTable";
import { SHAPE_TYPE, VISIBILITY } from "./utils/constants";
import {
  ENTRY_KIND,
  entryKindDashArray,
  entryTypeMarkerIcon,
  getEntryKindLabel,
  getLocationEntryKind,
  getMarkerHoverLabel,
  nearestMatchingDecade,
  normalizeAddressForMatch,
  MARKER_NEUTRAL_CURRENT,
  MARKER_NEUTRAL_OTHER,
} from "./utils/map.util";
import { getDistanceFromLatLng } from "./utils/geo";
import Toastr from "./Toastr";
import OnboardingPopup from "./OnboardingPopup/OnboardingPopup";
import useOnboardingDismissal from "./OnboardingPopup/useOnboardingDismissal";
import OnboardingWizard from "./OnboardingWizard/OnboardingWizard";
import WizardContribution from "./Wizard/WizardContribution";
import OnboardingTour, { TOUR_FLOWS } from "./OnboardingTour/OnboardingTour";
import { useIdleTimer } from "react-idle-timer";
import { ws } from "./theme";

function valuetext(value) {
  return `${value}`;
}

/**
 * Detects if an address is a Google Plus Code (e.g., "86QRP4M5+RG")
 * Plus Codes are alphanumeric location codes that aren't user-friendly
 */
function isPlusCode(address) {
  if (!address || typeof address !== "string") return false;
  // Plus codes look like: 86QRP4M5+RG or with locality suffix like "86QRP4M5+RG, City, Country"
  // Pattern: 4-8 alphanumeric chars + plus sign + 2+ alphanumeric chars
  const plusCodePattern = /^[A-Z0-9]{4,8}\+[A-Z0-9]{2,}/i;
  return plusCodePattern.test(address.trim());
}

function MapComponent() {
  const { preferences } = useUserPreferences();
  const mapRef = useRef();
  const searchBarRef = useRef();
  const mapInitializedRef = useRef(false);
  const addPlaceButtonRef = useRef(null);
  const timelineSliderRef = useRef(null);
  const browseTourStartYearRef = useRef(null);
  const prevTourFlowRef = useRef(null);
  const { globalRoles, projectRoles, user, loading, logout, setExhibitionMode, profile, isNewUser, markOnboardingComplete, authError, clearAuthError } = useAuth();
  const [isExhibitionMode, setIsExhibitionMode] = useState(() => {
    const params = new URLSearchParams(window?.location?.search);
    return params?.get("mode") === "exhibition";
  });
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [profileAnchorEl, setProfileAnchorEl] = React.useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const { project } = useProject();
  // Thematic filtering (Phase 3 #9; active-lens model, catalogue v3).
  // The map reads two things off the filter context: shapePassesFilter
  // (per-shape gate that hides out-of-filter shapes through the active
  // lens) and colorForShape (per-shape paint color through the active
  // lens). colorForShape now runs UNCONDITIONALLY — assigned taxonomy
  // colors paint in every view, not just when a chip is selected — so the
  // old isActiveLensFiltered gate is gone (colorForShape already returns
  // the neutral default when the shape carries no active-kind chip).
  const {
    shapePassesFilter,
    colorForShape,
    regionsVisible,
    setRegionsVisible,
    entryTypes,
    selectedContributorIds,
    showMyPlaces,
    setShowMyPlaces,
    highlightMyPlaces,
    setHighlightMyPlaces,
  } = useFilter();
  const { isDismissed: onboardingDismissed, dismiss: dismissOnboarding } =
    useOnboardingDismissal(project?.projectsid);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  // Contributors directory drawer (June 2026 slim-down): the full
  // contributors list, opened from the "Contributor" link in the
  // FilterBar. Stable close callback so RightDrawer's Esc listener
  // identity stays steady.
  const [contributorsDirectoryOpen, setContributorsDirectoryOpen] =
    useState(false);
  const closeContributorsDirectory = useCallback(
    () => setContributorsDirectoryOpen(false),
    [],
  );
  const [mapCenter, setMapCenter] = useState([
    project.center_lat,
    project.center_lng,
  ]);
  const [zoom, setZoom] = useState(project.zoom_level);
  const navigate = useNavigate();
  // NOTE: showMyPlaces moved into FilterContext (DisplayControlsBar) so
  // the top-right toolbar and the marker render loop share a single
  // source of truth and the user's preference survives reloads. See
  // src/FilterContext.js for the persistence keys.

  useEffect(() => {
    if (project.center_lat && project.center_lng && project.zoom_level) {
      setMapCenter([project.center_lat, project.center_lng]);
      setZoom(project.zoom_level);
    }
  }, [project]);

  useEffect(() => {
    const baseLayer = baseLayerOptions[preferences.baseLayer];
    setSelectedBaseLayer(baseLayer);
  }, [preferences.baseLayer]);

  useEffect(() => {
    const params = new URLSearchParams(window?.location?.search);
    const modeParam = params?.get("mode") === "exhibition";
    if (modeParam && project?.site_type !== "exhibition") {
      params.delete("mode");
      const newSearch = params.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "");
      window.history.replaceState({}, "", newUrl);
      setIsExhibitionMode(false);
      setExhibitionMode((prev) => prev === false ? prev : false);
    } else {
      const active = modeParam && project?.site_type === "exhibition";
      setIsExhibitionMode(active);
      setExhibitionMode((prev) => prev === active ? prev : active);
    }
  }, [project?.site_type, setExhibitionMode]);
  const [year, setYear] = useState(1950);

  // Outline color for an out-of-era (hollow) marker.
  //
  // Client request Aug 4 2026: "keep some way of ghosting sites for non-eras
  // even when theme filters are employed", and "color-code the boxes
  // according to the selected thematic filter".
  //
  // We paint the hue of the NEAREST era that carries a paintable chip,
  // rather than grey-for-multi-match: grey already means "no theme on
  // record", so reusing it for "matches several themes" would make one
  // color mean both no information and more than we can show. Nearest-era
  // is deterministic and agrees with the hover label and the empty-era
  // popup, which already resolve to that same decade.
  //
  // Client follow-up Aug 5 2026: this runs with or without a chip
  // selection. Solid in-era squares already wear their taxonomy hue in the
  // unfiltered view (colorForShape is unconditional), so gating the ghosts
  // on isActiveLensFiltered made the map contradict itself — the same site
  // was orange under a Music filter but grey under "All". Grey now means
  // one thing only: no active-kind chip on record in any era.
  const outOfEraMarkerColor = useCallback(
    (loc) => {
      const hit = nearestMatchingDecade({
        loc,
        year,
        // An era matches when it would actually paint a hue. Reusing
        // colorForShape as the predicate is what stops the era we PICK and
        // the color we PAINT from ever disagreeing: it already scopes to
        // the active lens, narrows to the chip selection when there is
        // one, and refuses to paint catch-all ("Other") chips.
        matches: (ids) => colorForShape(ids, null, loc.theme_ranks) !== null,
      });
      if (!hit) return MARKER_NEUTRAL_OTHER;
      return colorForShape(
        hit.taxonomyIds,
        MARKER_NEUTRAL_OTHER,
        loc.theme_ranks,
      );
    },
    [colorForShape, year],
  );

  const [showContribution, setShowContribution] = useState(false);
  const [initialLatLng, setInitialLatLng] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState("");
  const [selectedAddressLatLng, setSelectedAddressLatLng] = useState(null);
  const [suggestedAddress, setSuggestedAddress] = useState("");
  const [suggestedLatLng, setSuggestedLatLng] = useState(null);
  const [suggestedAddresses, setSuggestedAddresses] = useState([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [isCustomLocation, setIsCustomLocation] = useState(false);
  const [customSelectMode, setCustomSelectMode] = useState(false);
  const [showSuggestionPopup, setShowSuggestionPopup] = useState(false);
  const getDefaultVisibility = useCallback(() => {
    const siteType = project?.site_type;
    if (siteType === 'exhibition') return VISIBILITY.PROJECT;
    if (siteType === 'city') return VISIBILITY.PUBLIC;
    return user ? VISIBILITY.PRIVATE : VISIBILITY.PUBLIC;
  }, [project?.site_type, user]);

  const [storyVisibility, setStoryVisibility] = useState(
    getDefaultVisibility(),
  );
  const [selectMode, setSelectMode] = useState(false);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [isSidePanelFullscreen, setIsSidePanelFullscreen] = useState(false);
  const [, setSelectedMarker] = useState(null);
  const [showPreferences, setShowPreferences] = useState(false);
  const [showLayersAdmin] = useState(false);
  const [showUpdateAdmin] = useState(false);
  const [showCommentAdmin] = useState(false);
  const [overlayOpacity, setOverlayOpacity] = useState(1);
  const [showWizard, setShowWizard] = useState(false);
  const [activeTourFlow, setActiveTourFlow] = useState(null);

  const clearBrowseTourIfActive = useCallback(() => {
    setActiveTourFlow((flow) => (flow === TOUR_FLOWS.browse ? null : flow));
  }, []);

  useEffect(() => {
    if (activeTourFlow === TOUR_FLOWS.browse && prevTourFlowRef.current !== TOUR_FLOWS.browse) {
      browseTourStartYearRef.current = year;
    }
    prevTourFlowRef.current = activeTourFlow;
  }, [activeTourFlow, year]);

  useEffect(() => {
    if (activeTourFlow !== TOUR_FLOWS.browse) return;
    if (browseTourStartYearRef.current == null) return;
    if (year !== browseTourStartYearRef.current) {
      setActiveTourFlow(null);
    }
  }, [year, activeTourFlow]);
  const [isModalOpen, setIsModalOpen] = useState(false); // State for login modal visibility
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const [showAboutDialog, setShowAboutDialog] = useState(false);
  const [openPopup] = useState(false);
  const [showOverlayOpacity] = useState(true);
  const [locations, setLocations] = useState([]);
  const [selectedMarkerIds, setSelectedMarkerIds] = useState([]);
  const [emptyEraPopup, setEmptyEraPopup] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [addStoryMode, setAddStoryMode] = useState(false);
  const [polygons, setPolygons] = useState([]);
  const [isDrawingPolygon, setIsDrawingPolygon] = useState(false);
  const [polygonCoordinates, setPolygonCoordinates] = useState([]);
  const [polygonData, setPolygonData] = useState(null);
  // Line tool state mirrors the polygon plumbing exactly — same JSON-array
  // loc_id shape, same in-progress drawingPoints buffer (reused via
  // selectionType/isDrawingLine guards), but lines are an open path with a
  // ≥2-point minimum and are finalized via double-click instead of
  // closing the ring.
  const [polylines, setPolylines] = useState([]);
  const [isDrawingLine, setIsDrawingLine] = useState(false);
  const [lineCoordinates, setLineCoordinates] = useState([]);
  const [lineData, setLineData] = useState(null);
  const [hoveredPolylineId, setHoveredPolylineId] = useState(null);
  const [selectedPolylineId, setSelectedPolylineId] = useState(null);
  const [selectionType, setSelectionType] = useState(SHAPE_TYPE.MARKER);
  const [activeTool, setActiveTool] = useState("point");
  const [showTableView, setShowTableView] = useState(false);
  // DISABLED: showOnlyVetted - uncomment when re-enabling vetted sites button
  // eslint-disable-next-line no-unused-vars
  const [showOnlyVetted, _setShowOnlyVetted] = useState(false);
  const [showOnlyMapGroup, setShowOnlyMapGroup] = useState(false);
  // DISABLED: joinRequestSent - uncomment when re-enabling join group button
  // eslint-disable-next-line no-unused-vars
  const [_joinRequestSent, setJoinRequestSent] = useState(false);
  const [joinRequestSubmitting, setJoinRequestSubmitting] = useState(false);
  const [joinRequestToast, setJoinRequestToast] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  // Lightweight toast for "Welcome — your profile is set up" after the
  // onboarding wizard finishes. We keep it separate from joinRequestToast
  // so the two never fight for the same Snackbar slot.
  const [profileToast, setProfileToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Surface an auth/account-setup failure (from authContext) as a toast so a
  // failed users-row create is never silently swallowed. We clear the context
  // error right after showing it so the same failure re-notifies if it recurs
  // on a later onAuthStateChanged, without re-firing on every render.
  useEffect(() => {
    if (authError) {
      setProfileToast({ open: true, severity: "error", message: authError });
      clearAuthError();
    }
  }, [authError, clearAuthError]);

  const [drawingPoints, setDrawingPoints] = useState([]);
  // "Show Regions" used to live in local state with default false. It
  // now reads from FilterContext.regionsVisible so the same boolean is
  // shared with the FilterBar's "Display Regions" toggle (More Filters
  // panel). Default is OFF (changed in 2026-05-13 follow-up — polygon
  // overlays clutter the map on first load) and the value persists
  // across reloads via
  // localStorage — both behaviors required by the 2026-05-13 design
  // review. The legacy toolbar Show/Hide button is currently commented
  // out (the in-strip toggle is the sole source of truth), so we only
  // need a read-only `showRegions` alias here.
  const showRegions = regionsVisible;
  // Retained as no-op to minimize churn if the legacy block is ever
  // un-commented. Removing entirely would require restoring it.
  // eslint-disable-next-line no-unused-vars
  const _setShowRegions = useCallback(
    (next) => {
      if (typeof next === "function") {
        setRegionsVisible(next(regionsVisible));
      } else {
        setRegionsVisible(next);
      }
    },
    [regionsVisible, setRegionsVisible],
  );
  const passesShowMyPlaces = useCallback(
    (hasMine) => {
      // Anonymous/exhibition viewers never have an owner scope.
      if (!user) return true;
      if (showMyPlaces === SHOW_MY_PLACES_MODE.ONLY_MINE) return !!hasMine;
      if (showMyPlaces === SHOW_MY_PLACES_MODE.HIDE_MINE) return !hasMine;
      return true; // ALL
    },
    [user, showMyPlaces],
  );
  // "Show My Places" highlight (client feedback #8, re-clarified July 2026).
  // A PURE, OPT-IN highlight driven by its own boolean (highlightMyPlaces),
  // decoupled from the showMyPlaces FILTER. Default OFF: the user's sites
  // render as normal markers until they click "Show my places", which ADDS
  // large black pins on top of their own sites (raised above the others).
  // Clicking again ("Stop Showing My Places") removes the black pins but
  // never hides any place. This restores the original design and fixes the
  // "hide" misnomer the client called out.
  const isShowingMyPlaces = !!user && highlightMyPlaces;
  const [hoveredPolygonId, setHoveredPolygonId] = useState(null);
  const [selectedPolygonId, setSelectedPolygonId] = useState(null);
  // ISSUE-004: when the wizard / legacy form successfully posts a story we
  // open the side panel pre-focused on the new shape so the submitter can
  // immediately see the result. SidePanelComponent reads this id when its
  // next data load completes, lands on the matching page, then calls
  // onPendingFocusConsumed so we clear it back to null.
  const [pendingFocusShapeId, setPendingFocusShapeId] = useState(null);
  const hoveredPolygonIdRef = useRef(null);
  const polygonClickedRef = useRef(false);
  const polylineClickedRef = useRef(false);
  // Tracks the most recent line-drawing click so a follow-up click at the
  // same spot (i.e. a real dblclick) doesn't add a duplicate vertex right
  // before the dblclick handler finishes the line.
  const lastLineClickRef = useRef({ time: 0, latlng: null });
  const scrollContainerRef = useRef(null);
  const yearInitializedRef = useRef(false);
  const fetchAbortRef = useRef(null);
  // Shareable URL plumbing (PR #2): set true once the URL-driven
  // restore-state effect has had a chance to run on first data-load.
  // The URL-write effect gates on this so the default empty state
  // doesn't clobber incoming ?marker=/?polygon=/?year= params before
  // we've had a chance to honor them.
  const urlRestoredRef = useRef(false);

  const handlePendingFocusConsumed = useCallback(() => {
    setPendingFocusShapeId(null);
  }, []);

  // Stable callback so the wizard's confirmation-close handler can pop the
  // side panel onto the new shape without depending on lots of map state.
  const handleSubmissionSuccess = useCallback(
    ({ shapeType, shapeId } = {}) => {
      if (!shapeId) return;
      const numericId = Number(shapeId);
      if (Number.isNaN(numericId)) return;

      if (shapeType === SHAPE_TYPE.POLYGON) {
        // SidePanelComponent's polygon path tolerates a polygon row that
        // hasn't appeared in `polygons` yet (refreshData is async): when
        // currentPolygon is undefined it falls through to the raw id fetch
        // against getPolygonData.php, which already groups by coordinates.
        setEmptyEraPopup(null);
        setSelectedAddressLatLng(null);
        setSelectedAddress(null);
        setPolygonData({ id: numericId, coordinates: [] });
        setLineData(null);
        setSelectedPolylineId(null);
        setSelectedPolygonId(numericId);
        setSelectionType(SHAPE_TYPE.POLYGON);
      } else if (shapeType === SHAPE_TYPE.LINE) {
        // Lines mirror polygons here — getPolygonData.php with
        // shape_type=line tolerates a not-yet-refreshed polylines array.
        setEmptyEraPopup(null);
        setSelectedAddressLatLng(null);
        setSelectedAddress(null);
        setLineData({ id: numericId, coordinates: [] });
        setPolygonData(null);
        setSelectedPolygonId(null);
        setSelectedPolylineId(numericId);
        setSelectionType(SHAPE_TYPE.LINE);
      } else {
        setSelectedMarkerIds([numericId]);
        setPolygonData(null);
        setLineData(null);
        setSelectedPolygonId(null);
        setSelectedPolylineId(null);
        setSelectionType(SHAPE_TYPE.MARKER);
      }
      setPendingFocusShapeId(numericId);
      setIsSidePanelOpen(true);
    },
    [],
  );

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setIsMenuOpen(true);
  };

  const handleMenuClose = () => {
    setIsMenuOpen(false);
    setAnchorEl(null);
  };
  const handleProfileMenuOpen = (event) => {
    setProfileAnchorEl(event.currentTarget);
    setIsProfileMenuOpen(true);
  };

  const handleProfileMenuClose = () => {
    setIsProfileMenuOpen(false);
    setProfileAnchorEl(null);
  };

  const adminOrMod = () => {
    const projectId = project?.projectid;
    if (!projectId) return false;
    const projectAdminRole = projectId + "_Admin";
    const projectModRole = projectId + "_Mod";

    return (
      globalRoles?.isAdmin ||
      (projectRoles &&
        projectRoles.length > 0 &&
        (projectRoles.includes(projectAdminRole) || projectRoles.includes(projectModRole)))
    );
  };

  const canViewTable = () => {
    const projectId = project?.projectid;
    if (!projectId) return false;
    const tableRoles = new Set([
      `${projectId}_Admin`,
      `${projectId}_Mod`,
    ]);

    return (
      globalRoles?.isAdmin ||
      (projectRoles &&
        projectRoles.length > 0 &&
        projectRoles.some((role) => tableRoles.has(role)))
    );
  };

  const isMapGroupMember = useMemo(() => {
    const projectId = project?.projectid;
    if (!user || !projectId) return false;
    if (globalRoles?.isAdmin) return true;
    return projectRoles?.some((role) => role.startsWith(`${projectId}_`));
  }, [globalRoles, project?.projectid, projectRoles, user]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft =
        scrollContainerRef.current.scrollWidth;
    }
  }, []);

  useEffect(() => {
    if (!isMapGroupMember && showOnlyMapGroup) {
      setShowOnlyMapGroup(false);
    }
  }, [isMapGroupMember, showOnlyMapGroup]);

  const fetchJoinRequestStatus = useCallback(async () => {
    if (!user?.uid || !project?.projectsid) {
      setJoinRequestSent(false);
      return;
    }
    try {
      // Auth refactor 2026-05-27: caller uid now comes from the Firebase
      // ID token via apiCall(); the server checks the verified uid's own
      // pending request rather than trusting one sent in the body.
      const response = await apiCall("joinRequests.php", {
        method: "POST",
        body: {
          action: "check",
          projectsid: project.projectsid,
        },
      });
      setJoinRequestSent(Boolean(response?.pending));
    } catch (error) {
      console.error("Error checking join request:", error);
      setJoinRequestSent(false);
    }
  }, [project?.projectsid, user?.uid]);

  useEffect(() => {
    fetchJoinRequestStatus();
  }, [fetchJoinRequestStatus]);

  // Reset selectMode when contribution panel is closed
  useEffect(() => {
    if (!showContribution) {
      setSelectMode(false);
      setIsDrawingPolygon(false);
      setIsDrawingLine(false);
      setDrawingPoints([]);
      setPolygonCoordinates([]);
      setLineCoordinates([]);
      setActiveTool("point");
      setSelectionType(SHAPE_TYPE.MARKER);
      setSelectedPolygonId(null);
      setSelectedPolylineId(null);
      setPolygonData(null);
      setLineData(null);
      setCustomSelectMode(false);
    }
  }, [showContribution]);

  useEffect(() => {
    if (!isSidePanelOpen && !addStoryMode) {
      setSelectedPolygonId(null);
      setSelectedPolylineId(null);
      setPolygonData(null);
      setLineData(null);
      setPolygonCoordinates([]);
      setLineCoordinates([]);
      setSelectionType(SHAPE_TYPE.MARKER);
    }
  }, [isSidePanelOpen, addStoryMode]);

  useEffect(() => {
    if (showContribution && !addStoryMode) {
      setIsCustomLocation(false);
      setSelectedAddress(null);
      setCustomSelectMode(false);
    }
  }, [showContribution, addStoryMode]);
  useEffect(() => {
    if (!showContribution && !addStoryMode) {
      setStoryVisibility(getDefaultVisibility());
    }
  }, [user, showContribution, addStoryMode, getDefaultVisibility]);
  useEffect(() => {
    if (onboardingDismissed) {
      setOnboardingOpen(false);
    }
  }, [onboardingDismissed]);

  useEffect(() => {
    if (
      !loading &&
      project?.projectsid &&
      !onboardingDismissed
    ) {
      setOnboardingOpen(true);
    }
  }, [loading, project?.projectsid, onboardingDismissed]);

  // Reset all UI state to defaults (used by exhibition mode idle reset)
  const resetToDefaultState = useCallback(() => {
    // Close panels
    setIsSidePanelOpen(false);
    setIsSidePanelFullscreen(false);
    setShowContribution(false);
    setShowWizard(false);
    setShowPreferences(false);
    setShowTableView(false);

    // Reset overlays
    setSelectedLayer(null);
    setOverlayOpacity(1);

    // Close menus/dialogs
    setIsModalOpen(false);
    setIsMenuOpen(false);
    setIsProfileMenuOpen(false);
    setShowSuggestionPopup(false);
    setShowHelpDialog(false);
    setShowAboutDialog(false);

    // Reset selection/drawing state
    setSelectMode(false);
    setSelectedMarkerIds([]);
    setSelectedPolygonId(null);
    setSelectedPolylineId(null);
    setIsDrawingPolygon(false);
    setIsDrawingLine(false);
    setDrawingPoints([]);
    setPolygonCoordinates([]);
    setLineCoordinates([]);
    setPolygonData(null);
    setLineData(null);
    setSelectionType(SHAPE_TYPE.MARKER);
    setActiveTool("point");
    setIsEditing(false);
    setAddStoryMode(false);

    // Reset address/location
    setSelectedAddress("");
    setSelectedAddressLatLng(null);
    setSuggestedAddress("");
    setSuggestedLatLng(null);
    setSuggestedAddresses([]);
    setSelectedSuggestion(null);
    setIsCustomLocation(false);
    setCustomSelectMode(false);

    // Reset filters
    setShowMyPlaces(SHOW_MY_PLACES_MODE.ALL);
    setShowOnlyMapGroup(false);
    setHoveredPolygonId(null);

    // Reset map view to project defaults
    if (project?.center_lat && project?.center_lng) {
      setMapCenter([project.center_lat, project.center_lng]);
    }
    if (project?.zoom_level) {
      setZoom(project.zoom_level);
    }

    // Reset year to default (1950)
    setYear(1950);
  }, [project, setShowMyPlaces]);

  useEffect(() => {
    if (!isSidePanelOpen && !showContribution && !showPreferences && !showTableView && !emptyEraPopup) {
      searchBarRef.current?.clear();
    }
  }, [isSidePanelOpen, showContribution, showPreferences, showTableView, emptyEraPopup]);

  // Exhibition mode idle reset - reopens onboarding popup after inactivity
  useIdleTimer({
    timeout: 10 * 60 * 1000,
    onIdle: () => {
      if (isExhibitionMode) {
        resetToDefaultState();
        setOnboardingOpen(true);
      }
    },
    disabled: !isExhibitionMode, // Only run in exhibition mode
    events: [
      "mousemove",
      "keydown",
      "wheel",
      "mousedown",
      "touchstart",
      "touchmove",
      "click",
      "scroll",
    ],
    startOnMount: true,
  });

  useEffect(() => {
    if (!mapRef.current) return;
    const leafletContainer = mapRef.current.getContainer();
    if (activeTool === SHAPE_TYPE.POLYGON) {
      leafletContainer.classList.add("drawing-polygon");
    } else {
      leafletContainer.classList.remove("drawing-polygon");
    }
    if (activeTool === SHAPE_TYPE.LINE) {
      leafletContainer.classList.add("drawing-line");
    } else {
      leafletContainer.classList.remove("drawing-line");
    }
    return () => {
      leafletContainer.classList.remove("drawing-polygon");
      leafletContainer.classList.remove("drawing-line");
    };
  }, [activeTool]);

  // getDistanceFromLatLng imported from utils/geo.js

  const marks = [
    {
      value: 1760,
      label: (
        <Paper
          elevation={2}
          radius={3}
          sx={{
            paddingTop: "0",
            paddingBottom: "0",
            paddingLeft: "0",
            paddingRight: "0",
            boxShadow: "unset",
            filter:
              openPopup === true
                ? "grayscale(100) opacity(1) brightness(0.5)"
                : "none",
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: "#413A36", fontSize: "0.688rem", fontWeight: "600" }}
          >
            Pre-1770
          </Typography>
        </Paper>
      ),
    },
    { value: 1770, label: "1770s" },
    { value: 1780, label: "1780s" },
    { value: 1790, label: "1790s" },
    { value: 1800, label: "1800s" },
    { value: 1810, label: "1810s" },
    { value: 1820, label: "1820s" },
    { value: 1830, label: "1830s" },
    { value: 1840, label: "1840s" },
    { value: 1850, label: "1850s" },
    { value: 1860, label: "1860s" },
    { value: 1870, label: "1870s" },
    { value: 1880, label: "1880s" },
    { value: 1890, label: "1890s" },
    { value: 1900, label: "1900s" },
    { value: 1910, label: "1910s" },
    { value: 1920, label: "1920s" },
    { value: 1930, label: "1930s" },
    { value: 1940, label: "1940s" },
    { value: 1950, label: "1950s" },
    { value: 1960, label: "1960s" },
    { value: 1970, label: "1970s" },
    { value: 1980, label: "1980s" },
    { value: 1990, label: "1990s" },
    { value: 2000, label: "2000s" },
    { value: 2010, label: "2010s" },
    { value: 2020, label: "2020s" },
    {
      value: 2030,
      label: (
        <Paper
          elevation={2}
          radius={3}
          sx={{
            paddingTop: "0",
            paddingBottom: "0",
            paddingLeft: "0",
            paddingRight: "0",
            boxShadow: "unset",
            filter:
              openPopup === true
                ? "grayscale(100) opacity(1) brightness(0.5)"
                : "none",
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: "#413A36", fontSize: "0.688rem", fontWeight: "600" }}
          >
            Vision
          </Typography>
        </Paper>
      ),
    },
  ];
  const fetchLocations = useCallback(async () => {
    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    try {
      // Auth refactor 2026-05-27: dropped &uid= — the viewer's uid is now
      // resolved server-side from the verified Firebase ID token attached
      // by apiCall(). Anonymous viewers send no token and get the
      // public-only visibility path.
      const query = { projectsid: project.projectsid };
      if (isExhibitionMode) query.mode = "exhibition";
      // Type of Entry (D-LOCK-1, multi-select). The backend accepts
      // an `entry_types` CSV — empty CSV = strict "show nothing" (a
      // legitimate user state when every pill is off), all 3 ids =
      // no constraint. We always send the CSV when the Set is
      // non-default so the server doesn't fall back to the legacy
      // single-value `entry_type` path. Default (Personal + Verified)
      // is sent too — the server short-circuits when all-or-default
      // semantics apply, but being explicit keeps the request log
      // readable when debugging.
      if (entryTypes instanceof Set) {
        query.entry_types = Array.from(entryTypes).sort().join(",");
      }
      // Contributor multi-select. Send a CSV of numeric users.id (the
      // backend whitelists each value). Skipped entirely when empty so
      // the URL stays clean for the default case.
      if (Array.isArray(selectedContributorIds) && selectedContributorIds.length > 0) {
        query.contributor_ids = selectedContributorIds
          .map((n) => String(n))
          .join(",");
      }

      const data = await apiCall("selectProjectCont.php", {
        query,
        signal: controller.signal,
      });
      if (!controller.signal.aborted) {
        setLocations(data.locations);
        setPolygons(data.polygons);
        // selectProjectCont.php now returns a sibling `polylines` array
        // alongside polygons. Default to [] so existing deployments without
        // the line backend still render the rest of the map cleanly.
        setPolylines(data.polylines || []);
      }
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("Error fetching data:", error);
      }
    }
  }, [
    project.projectsid,
    isExhibitionMode,
    entryTypes,
    selectedContributorIds,
  ]);

  useEffect(() => {
    if (loading) return;
    // Passing the auth epoch makes this effect explicitly depend on the
    // signed-in identity without ever sending a client-supplied uid. The
    // callback reads the verified token through apiCall().
    fetchLocations(user?.uid);
    return () => {
      if (fetchAbortRef.current) {
        fetchAbortRef.current.abort();
      }
    };
  }, [fetchLocations, loading, user?.uid]);

  // ── Deep-link restoration ────────────────────────────────────────────
  // Shareable URL contract (PR #2 of the staging-QA batch). A city URL
  // can carry:
  //   ?marker=<cont_id>    → opens the marker cluster containing that cont
  //   ?polygon=<poly_id>   → opens that polygon
  //   ?polyline=<line_id>  → opens that polyline
  //   ?year=<decade>       → sets the decade slider (also written on every
  //                          shape change so the link round-trips)
  // We wait until at least one of the three data classes has loaded so
  // the lookup tables are populated; otherwise the lookups all miss
  // silently and the panel never opens. Runs exactly once per mount
  // (guarded by urlRestoredRef). If the encoded id doesn't resolve
  // (deleted row, private without auth, wrong project) we fall through
  // to the default landing state — a half-open panel would be worse UX
  // than just landing on the city.
  useEffect(() => {
    if (urlRestoredRef.current) return;
    if (!project?.projectsid) return;
    if (locations.length === 0 && polygons.length === 0 && polylines.length === 0) return;

    const params = new URLSearchParams(window.location.search);
    const markerId = parseInt(params.get("marker"), 10);
    const polygonId = parseInt(params.get("polygon"), 10);
    const polylineId = parseInt(params.get("polyline"), 10);
    const yearParam = parseInt(params.get("year"), 10);

    urlRestoredRef.current = true;

    if (Number.isFinite(yearParam)) {
      const decade = Math.floor(yearParam / 10) * 10;
      if (decade >= 1900 && decade <= 2020) {
        setYear(decade);
      }
    }

    if (Number.isFinite(markerId)) {
      const cluster = locations.find(
        (loc) => Array.isArray(loc.ids) && loc.ids.includes(markerId),
      );
      if (cluster) {
        setSelectedMarkerIds([cluster.ids]);
        setSelectedMarker(cluster.ids);
        if (
          typeof cluster.loc_lat === "number" &&
          typeof cluster.loc_lng === "number"
        ) {
          setSelectedAddressLatLng({
            lat: cluster.loc_lat,
            lng: cluster.loc_lng,
          });
          setMapCenter([cluster.loc_lat, cluster.loc_lng]);
        }
        setPendingFocusShapeId(markerId);
        setIsSidePanelOpen(true);
      }
      return;
    }

    if (Number.isFinite(polygonId)) {
      const poly = polygons.find((p) => p.id === polygonId);
      if (poly) {
        setPolygonData({
          id: polygonId,
          coordinates: poly.coordinates || [],
        });
        setSelectedPolygonId(polygonId);
        setSelectionType(SHAPE_TYPE.POLYGON);
        setPendingFocusShapeId(polygonId);
        setIsSidePanelOpen(true);
        if (Array.isArray(poly.coordinates) && poly.coordinates.length > 0) {
          const lats = poly.coordinates.map((c) => c[0]);
          const lngs = poly.coordinates.map((c) => c[1]);
          setMapCenter([
            lats.reduce((a, b) => a + b, 0) / lats.length,
            lngs.reduce((a, b) => a + b, 0) / lngs.length,
          ]);
        }
      }
      return;
    }

    if (Number.isFinite(polylineId)) {
      const line = polylines.find((p) => p.id === polylineId);
      if (line) {
        setLineData({
          id: polylineId,
          coordinates: line.coordinates || [],
        });
        setSelectedPolylineId(polylineId);
        setSelectionType(SHAPE_TYPE.LINE);
        setPendingFocusShapeId(polylineId);
        setIsSidePanelOpen(true);
        if (Array.isArray(line.coordinates) && line.coordinates.length > 0) {
          const lats = line.coordinates.map((c) => c[0]);
          const lngs = line.coordinates.map((c) => c[1]);
          setMapCenter([
            lats.reduce((a, b) => a + b, 0) / lats.length,
            lngs.reduce((a, b) => a + b, 0) / lngs.length,
          ]);
        }
      }
    }
  }, [project?.projectsid, locations, polygons, polylines]);

  // ── URL sync ─────────────────────────────────────────────────────────
  // Keep the address bar in sync with the side panel selection + decade,
  // so reloading the page or copying the URL hands the recipient the
  // same view. replaceState (not push) so each map click doesn't stack
  // a browser-history entry — back button still does whole-page nav.
  //
  // Gated on urlRestoredRef so the first render doesn't clobber an
  // incoming deep link before it's been consumed.
  useEffect(() => {
    if (!urlRestoredRef.current) return;

    const params = new URLSearchParams(window.location.search);
    // Wipe shape + year params on every write, then re-add whichever
    // applies. This keeps unrelated params (mode=exhibition, etc.)
    // untouched while preventing a stale ?marker= from sitting next
    // to a fresh ?polygon=.
    params.delete("marker");
    params.delete("polygon");
    params.delete("polyline");
    params.delete("year");

    if (isSidePanelOpen) {
      // Branch on selectionType, not on which arrays happen to be
      // populated. Polygon/polyline clicks also set selectedMarkerIds
      // (every marker inside the shape), so a naive "marker first"
      // priority would mis-encode a polygon click as ?marker=. The
      // selectionType is the single source of truth for which shape
      // the panel currently represents.
      let encoded = false;
      if (selectionType === SHAPE_TYPE.POLYGON && selectedPolygonId != null) {
        params.set("polygon", String(selectedPolygonId));
        encoded = true;
      } else if (selectionType === SHAPE_TYPE.LINE && selectedPolylineId != null) {
        params.set("polyline", String(selectedPolylineId));
        encoded = true;
      } else if (selectedMarkerIds && selectedMarkerIds.length > 0) {
        // selectedMarkerIds is either [id,id,id] (flat) or [[id,id,id]]
        // (nested for a cluster); peel either shape down to the first
        // numeric id.
        const flat = Array.isArray(selectedMarkerIds[0])
          ? selectedMarkerIds[0]
          : selectedMarkerIds;
        const first = flat[0];
        if (Number.isFinite(first)) {
          params.set("marker", String(first));
          encoded = true;
        }
      }
      if (encoded) {
        params.set("year", String(Math.floor(year / 10) * 10));
      }
    }

    const newSearch = params.toString();
    const newUrl =
      window.location.pathname + (newSearch ? `?${newSearch}` : "");
    const currentUrl = window.location.pathname + window.location.search;
    if (newUrl !== currentUrl) {
      window.history.replaceState({}, "", newUrl);
    }
  }, [
    isSidePanelOpen,
    selectionType,
    selectedMarkerIds,
    selectedPolygonId,
    selectedPolylineId,
    year,
  ]);

  // Dismissing the empty-era / "No stories" popup must ALWAYS clear the
  // temporary black search pin (selectedAddressLatLng) along with it.
  //
  // THREE paths dismiss this popup and they must not drift apart:
  //   1. the X button          (handleClose, in the popup renderer)
  //   2. a click on the map    (map click handler)
  //   3. moving the timeline   (the year-change effect below)
  //
  // Only path 1 used to clear the pin, so a user who dismissed the popup by
  // clicking the map — which is what the client actually does — was left with
  // an orphaned black marker until a page refresh. That is the re-reported
  // "Marker Doesn't Go Away" bug: the fix looked present in the code because
  // handleClose was correct, while the two common paths were not.
  //
  // Deliberately NOT routed through here: handleJumpBack / handleJumpForward /
  // handlePopupAddPlace. Those keep the user at the same location and re-set
  // the pin themselves.
  const dismissEmptyEraPopup = () => {
    setEmptyEraPopup(null);
    setSelectedAddressLatLng(null);
    setSelectedAddress(null);
    searchBarRef.current?.clear();
  };

  useEffect(() => {
    if (!yearInitializedRef.current) {
      yearInitializedRef.current = true;
      return;
    }

    if (emptyEraPopup) {
      dismissEmptyEraPopup();
      return;
    }

    if (isSidePanelOpen && polygonData?.id && Array.isArray(polygons)) {
      const currentPolygon = polygons.find((p) => p.id === polygonData.id);
      if (currentPolygon) {
        const currentDecade = Math.floor(year / 10) * 10;
        const currentCoords = JSON.stringify(currentPolygon.coordinates);

        const matchForDecade = polygons.find(
          (p) =>
            JSON.stringify(p.coordinates) === currentCoords &&
            Math.floor(Number(p.year) / 10) * 10 === currentDecade,
        );

        if (!matchForDecade) {
          const allDecades = new Set();
          polygons.forEach((p) => {
            if (JSON.stringify(p.coordinates) === currentCoords) {
              allDecades.add(Math.floor(Number(p.year) / 10) * 10);
            }
          });

          const coords = polygonData.coordinates;
          const centroid = coords.reduce(
            (acc, [lat, lng]) => ({ lat: acc.lat + lat / coords.length, lng: acc.lng + lng / coords.length }),
            { lat: 0, lng: 0 },
          );

          setIsSidePanelOpen(false);
          setEmptyEraPopup({
            lat: centroid.lat,
            lng: centroid.lng,
            availableYears: Array.from(allDecades).sort((a, b) => a - b),
            markerIds: [],
            polygonInfo: { id: polygonData.id, coordinates: polygonData.coordinates, rawCoordinates: currentPolygon.coordinates },
          });
        }
      }
    } else if (isSidePanelOpen && selectedMarkerIds?.length > 0) {
      const flatIds = selectedMarkerIds.flat();
      const currentDecade = Math.floor(year / 10) * 10;
      const matchingLocs = locations.filter((loc) =>
        loc.ids.some((id) => flatIds.includes(id)),
      );
      const hasContent = matchingLocs.some((loc) =>
        loc.years.some((y) => Math.floor(y / 10) * 10 === currentDecade),
      );

      if (!hasContent && matchingLocs.length > 0) {
        const allDecades = new Set();
        matchingLocs.forEach((loc) =>
          loc.years.forEach((y) => allDecades.add(Math.floor(y / 10) * 10)),
        );
        setIsSidePanelOpen(false);
        setEmptyEraPopup({
          lat: matchingLocs[0].loc_lat,
          lng: matchingLocs[0].loc_lng,
          availableYears: Array.from(allDecades).sort((a, b) => a - b),
          markerIds: selectedMarkerIds,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const handleOverlayOpacityChange = (event, newValue) => {
    setOverlayOpacity(newValue);
  };

  // DISABLED: handleJoinRequest - uncomment when re-enabling join group button
  // eslint-disable-next-line no-unused-vars
  const _handleJoinRequest = () => {
    if (!user) {
      handleLogin();
      return;
    }
    if (!project?.projectsid || joinRequestSubmitting) return;

    setJoinRequestSubmitting(true);
    // Auth refactor 2026-05-27: server now derives the requesting user from
    // the verified Firebase ID token (apiCall attaches it). Previously the
    // client passed firebase_uid in the body, which let a malicious caller
    // submit a join request attributed to anyone.
    apiCall("joinRequests.php", {
      method: "POST",
      body: {
        action: "create",
        projectsid: project.projectsid,
      },
    })
      .then((response) => {
        if (response?.already_exists) {
          setJoinRequestSent(true);
          setJoinRequestToast({
            open: true,
            message: "Your request is already pending.",
            severity: "info",
          });
          return;
        }
        setJoinRequestSent(true);
        setJoinRequestToast({
          open: true,
          message: "Join request submitted.",
          severity: "success",
        });
      })
      .catch((error) => {
        console.error("Error submitting join request:", error);
        setJoinRequestToast({
          open: true,
          message: "Failed to submit join request.",
          severity: "error",
        });
      })
      .finally(() => {
        setJoinRequestSubmitting(false);
      });
  };

  const handleSearchSelect = (event, result) => {
    if (!result || !result.lat || !result.lng) return;
    clearBrowseTourIfActive();
    setIsCustomLocation(false);
    setEmptyEraPopup(null);

    if (result.id) {
      if (result.year) {
        const resultDecade = Math.floor(Number(result.year) / 10) * 10;
        setYear(resultDecade);
      }
      // Bug fix: keyword-search previously locked selection to the single
      // matched story id. That broke decade-nav (pullsidepanel.php filters
      // by year, so switching decades on a single-id selection returns
      // empty content). Use the full sibling-ids array for the parent
      // location so decade-nav resolves to the era-appropriate story,
      // and use pendingFocusShapeId to land the carousel on the match.
      const parentLoc = locations.find(
        (loc) => Array.isArray(loc.ids) && loc.ids.includes(result.id),
      );
      if (parentLoc) {
        setSelectedMarkerIds([parentLoc.ids]);
        setSelectedMarker(parentLoc.ids);
      } else {
        setSelectedMarkerIds([result.id]);
        setSelectedMarker([result.id]);
      }
      setPendingFocusShapeId(result.id);
      setSelectedAddressLatLng({ lat: result.lat, lng: result.lng });
      setSelectedAddress(result.address);
      mapRef.current.setView([result.lat, result.lng], zoom);
      setIsSidePanelOpen(true);
      return;
    }

    // Match a searched address to an existing story. Two strategies, in
    // priority order (client bug #5, RE-REPORTED July 2026 — a widened 50m
    // radius alone still missed sites like "6750 Greenbriar Rd"):
    //   1. EXACT ADDRESS-STRING match. If the searched address equals a
    //      story's stored address, open that story regardless of geometric
    //      distance. A contributor's placed pin can sit hundreds of meters
    //      from where Google re-geocodes the same address string (common for
    //      suburban/rural parcels), so a story reached by searching its own
    //      address was falling through to the "No stories" popup while its
    //      marker sat far away. Address equality is the ground truth the
    //      user expects, so it takes precedence over distance.
    //   2. PROXIMITY fallback (~50m). For searches that don't exactly match
    //      a stored address but land near an existing cluster. NOTE:
    //      getDistanceFromLatLng returns KILOMETERS; we sort nearest-first
    //      so the closest cluster wins.
    // normalizeAddressForMatch expands street-type and directional
    // abbreviations and drops ZIP/country, so a stored "814 Atlas Ave,
    // Madison, WI 53714, US" matches Google's "814 Atlas Avenue, Madison,
    // WI, USA". The previous inline normalizer only lowercased and stripped
    // punctuation, so strategy 1 could never fire and every search silently
    // fell through to the 50m proximity check below.
    const searchedAddr = normalizeAddressForMatch(result.address);
    const addressLocations = searchedAddr
      ? locations.filter(
          (loc) => normalizeAddressForMatch(loc.address) === searchedAddr,
        )
      : [];

    const radius = 0.05;
    const proximityLocations = locations
      .filter(
        (loc) =>
          Number.isFinite(loc.loc_lat) && Number.isFinite(loc.loc_lng),
      )
      .map((loc) => ({
        loc,
        distance: getDistanceFromLatLng(
          result.lat,
          result.lng,
          loc.loc_lat,
          loc.loc_lng,
        ),
      }))
      .filter(({ distance }) => distance <= radius)
      .sort((a, b) => a.distance - b.distance)
      .map(({ loc }) => loc);

    // Exact address match wins; otherwise fall back to proximity.
    const isAddressMatch = addressLocations.length > 0;
    const nearbyLocations = isAddressMatch
      ? addressLocations
      : proximityLocations;

    // When we matched by address (not proximity), the geocoded point can be
    // far from the contributor's pin — center on the actual marker so the
    // opened story is visible and the search pin lands on it, not on an
    // empty patch of map.
    const primaryLoc = nearbyLocations[0];
    const focusLat =
      isAddressMatch && primaryLoc ? primaryLoc.loc_lat : result.lat;
    const focusLng =
      isAddressMatch && primaryLoc ? primaryLoc.loc_lng : result.lng;
    setSelectedAddressLatLng({ lat: focusLat, lng: focusLng });
    setSelectedAddress(result.address);
    mapRef.current.setView([focusLat, focusLng], zoom);

    if (nearbyLocations.length > 0) {
      const currentDecade = Math.floor(year / 10) * 10;
      const hasContentForEra = nearbyLocations.some((loc) =>
        loc.years.some((y) => Math.floor(y / 10) * 10 === currentDecade),
      );
      const nearbyMarkerIds = nearbyLocations.map((loc) => loc.ids);

      if (hasContentForEra) {
        const matchingLoc = nearbyLocations.find((loc) =>
          loc.years.some((y) => Math.floor(y / 10) * 10 === currentDecade),
        );
        setSelectedMarkerIds(matchingLoc ? [matchingLoc.ids] : nearbyMarkerIds);
        setSelectedMarker(matchingLoc ? matchingLoc.ids : null);
        setIsSidePanelOpen(true);
      } else {
        const allDecades = new Set();
        nearbyLocations.forEach((loc) =>
          loc.years.forEach((y) => allDecades.add(Math.floor(y / 10) * 10)),
        );
        // Use the marker-centered coords (focusLat/Lng) so an address match
        // to a different decade jumps to the real pin, not the far geocode.
        setEmptyEraPopup({
          lat: focusLat,
          lng: focusLng,
          availableYears: Array.from(allDecades).sort((a, b) => a - b),
          markerIds: nearbyMarkerIds,
        });
      }
    } else {
      setSelectedMarkerIds([]);
      setSelectedMarker(null);
      setEmptyEraPopup({
        lat: result.lat,
        lng: result.lng,
        availableYears: [],
        markerIds: [],
        noStories: true,
      });
    }
  };

  const clearSidePanel = () => {
    setIsSidePanelOpen(false);
    setEmptyEraPopup(null);
    setSelectedMarker(null);
    setSelectedMarkerIds([]);
    setSelectedAddress(null);
    setSelectedAddressLatLng(null);
    searchBarRef.current?.clear();
  };

  const handleAcceptSuggestion = () => {
    if (!selectedSuggestion && !suggestedAddress) {
      setShowSuggestionPopup(false);
      setSelectMode(false);
      return;
    }
    setIsCustomLocation(false);
    setCustomSelectMode(false);
    setSelectedAddress(suggestedAddress);
    // Keep marker at the exact clicked position, not the suggested address location
    setSelectedAddressLatLng(initialLatLng);
    setSuggestedAddress("");
    setSuggestedLatLng(null);
    setSuggestedAddresses([]);
    setSelectedSuggestion(null);
    setShowSuggestionPopup(false);
    setSelectMode(false);
  };

  const handleKeepCustom = () => {
    console.log("Keeping custom location", initialLatLng);
    setIsCustomLocation(true);
    setCustomSelectMode(true);
    // Set address to lat/lng coordinates for custom location
    const latLngAddress = `${initialLatLng.lat}, ${initialLatLng.lng}`;
    setSelectedAddress(latLngAddress);
    setSelectedAddressLatLng(initialLatLng);
    setSuggestedAddress("");
    setSuggestedLatLng(initialLatLng);
    setSuggestedAddresses([]);
    setSelectedSuggestion(null);
    setShowSuggestionPopup(false);
    setSelectMode(false);
  };

  const handleSelectSuggestion = (suggestion) => {
    setSelectedSuggestion(suggestion);
    if (suggestion) {
      setSuggestedAddress(suggestion.address);
      setSuggestedLatLng({ lat: suggestion.lat, lng: suggestion.lng });
    } else {
      setSuggestedAddress("");
      setSuggestedLatLng(null);
    }
  };

  // Component to update map center only once when REAL project data first loads
  const MapCenterUpdater = ({ center, zoomLevel, projectId }) => {
    const map = useMap();
    useEffect(() => {
      // Only set view once when valid project data is loaded (projectId is set)
      // Uses mapInitializedRef from parent to persist across re-renders
      if (!mapInitializedRef.current && projectId && center && center[0] && center[1] && zoomLevel) {
        map.setView(center, zoomLevel);
        mapInitializedRef.current = true;
      }
    }, [map, center, zoomLevel, projectId]);
    return null;
  };

  const MapEventsHandler = () => {
    const map = useMap();
    // While drawing a line we want a real dblclick to finish the path
    // instead of zooming the map. The polygon flow doesn't need this
    // because it closes via clicking the first vertex.
    useEffect(() => {
      if (!map) return;
      if (isDrawingLine) {
        map.doubleClickZoom.disable();
      } else {
        map.doubleClickZoom.enable();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, isDrawingLine]);
    useMapEvents({
      click: async (e) => {
        // Bug fix: any click on the map area (including shapes/markers via
        // the polygonClickedRef bypass below) should dismiss whatever
        // onboarding tour is active. Previously the addPlace tour could
        // only be dismissed by clicking the exact "Add a New Place"
        // button, which felt trapped.
        if (activeTourFlow) {
          setActiveTourFlow(null);
        }
        if (polygonClickedRef.current) {
          polygonClickedRef.current = false;
          return;
        }
        if (polylineClickedRef.current) {
          polylineClickedRef.current = false;
          return;
        }
        if (emptyEraPopup) {
          // Clears the black search pin too — see dismissEmptyEraPopup.
          dismissEmptyEraPopup();
        }
        if (isDrawingPolygon && activeTool === SHAPE_TYPE.POLYGON) {
          handlePolygonDrawClick(e.latlng);
          return;
        }
        if (isDrawingLine && activeTool === SHAPE_TYPE.LINE) {
          handleLineDrawClick(e.latlng);
          return;
        }
        // Click on empty map space (no shape, no drawing, no selectMode):
        // close the side panel and clear selection state. This makes the map
        // feel responsive when the user wants to dismiss whatever is open
        // without hunting for the X button.
        if (
          isSidePanelOpen &&
          !isDrawingOrPlacingShape &&
          !selectMode &&
          !showSuggestionPopup
        ) {
          setIsSidePanelOpen(false);
          setSelectedMarker(null);
          setSelectedMarkerIds([]);
          setPolygonData(null);
          setLineData(null);
          setSelectedPolygonId(null);
          setSelectedPolylineId(null);
          setSelectedAddress(null);
          setSelectedAddressLatLng(null);
          return;
        }
        if (selectMode && activeTool === "point") {
          const apiKey = config.GOOGLE_MAPS_API_KEY;
          const { lat, lng } = e.latlng;
          const nearThresholdKm = 0.01524; // 50ft
          setInitialLatLng({ lat, lng });

          // Snap to closest existing marker if within threshold
          let snappedLat = lat;
          let snappedLng = lng;
          if (locations && locations.length > 0) {
            let closestDist = Infinity;
            locations.forEach((loc) => {
              if (loc.loc_lat != null && loc.loc_lng != null) {
                const d = getDistanceFromLatLng(lat, lng, loc.loc_lat, loc.loc_lng);
                if (d < closestDist) {
                  closestDist = d;
                  if (d <= nearThresholdKm) {
                    snappedLat = loc.loc_lat;
                    snappedLng = loc.loc_lng;
                  }
                }
              }
            });
          }
          setSelectedAddressLatLng({ lat: snappedLat, lng: snappedLng });

          // Find the closest known address from Google
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`,
          );

          if (response.ok) {
            const geocodeData = await response.json();

            if (geocodeData.results && geocodeData.results.length > 0) {
              // Filter out Plus Codes from Google's results
              const filteredResults = geocodeData.results.filter(
                (result) => !isPlusCode(result.formatted_address)
              );

              if (filteredResults.length > 0) {
                const firstResult = filteredResults[0];
                const address = firstResult.formatted_address;
                const suggestionLat = firstResult.geometry.location.lat;
                const suggestionLng = firstResult.geometry.location.lng;

                // Calculate distance from click to suggested address
                const distanceToSuggestion = getDistanceFromLatLng(
                  lat, lng, suggestionLat, suggestionLng
                );

                if (!customSelectMode) {
                  // Regular "Select on Map" mode - directly use Google's first result, no popup
                  setSelectedAddress(address);
                  setSelectedAddressLatLng(e.latlng);
                  setSelectMode(false);
                } else {
                  // Custom select mode - check 50ft boundary
                  if (distanceToSuggestion <= nearThresholdKm) {
                    // Within 50ft - show popup with address suggestion
                    const suggestions = filteredResults.map((result) => ({
                      address: result.formatted_address,
                      lat: result.geometry.location.lat,
                      lng: result.geometry.location.lng,
                      placeId: result.place_id,
                    }));
                    setSuggestedAddresses(suggestions);
                    setSelectedSuggestion(suggestions[0]);
                    setSuggestedAddress(address);
                    setSuggestedLatLng({ lat: suggestionLat, lng: suggestionLng });
                    setShowSuggestionPopup(true);
                  } else {
                    // Beyond 50ft - directly use lat/lng, no popup
                    const latLngAddress = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
                    setSelectedAddress(latLngAddress);
                    setSelectedAddressLatLng(e.latlng);
                    setIsCustomLocation(true);
                    setCustomSelectMode(false);
                    setSelectMode(false);
                  }
                }
              } else {
                // Only Plus Codes (or filtered empty): coordinate string only in custom lat/long mode
                if (customSelectMode) {
                  const latLngAddress = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
                  setSelectedAddress(latLngAddress);
                  setSelectedAddressLatLng(e.latlng);
                  setIsCustomLocation(true);
                  setCustomSelectMode(false);
                } else {
                  setSelectedAddress("");
                  setSelectedAddressLatLng(e.latlng);
                  setIsCustomLocation(false);
                }
                setSelectMode(false);
              }
            } else {
              console.error("No results found for these coordinates");
              if (customSelectMode) {
                const latLngAddress = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
                setSelectedAddress(latLngAddress);
                setSelectedAddressLatLng(e.latlng);
                setIsCustomLocation(true);
                setCustomSelectMode(false);
              } else {
                setSelectedAddress("");
                setSelectedAddressLatLng(e.latlng);
                setIsCustomLocation(false);
              }
              setSelectMode(false);
            }
          } else {
            console.error(`Geocoding error: ${response.status}`);
            if (!customSelectMode) {
              setSelectedAddress("");
            }
            setSelectMode(false);
          }
        }
      },
      dblclick: (e) => {
        // Lines finish on double-click. We disable doubleClickZoom while
        // drawing (above) so this is purely a "finish" affordance.
        if (isDrawingLine && activeTool === SHAPE_TYPE.LINE) {
          finishLineDrawing(e.latlng);
        }
      },
      mousemove: (e) => {
        if (!showRegions) {
          if (hoveredPolygonIdRef.current !== null) {
            hoveredPolygonIdRef.current = null;
            setHoveredPolygonId(null);
          }
          return;
        }
        const nextHoveredId = getHoveredPolygonId(e.latlng);
        if (nextHoveredId !== hoveredPolygonIdRef.current) {
          hoveredPolygonIdRef.current = nextHoveredId;
          setHoveredPolygonId(nextHoveredId);
        }
      },
    });
    return null;
  };

  useEffect(() => {
    if (!mapRef.current) return;
    const leafletContainer = mapRef.current.getContainer();
    if (selectMode) {
      leafletContainer.classList.add("selecting");
    } else {
      leafletContainer.classList.remove("selecting");
    }
    return () => {
      leafletContainer.classList.remove("selecting");
    };
  }, [selectMode]);

  useEffect(() => {
    const geocodeAddress = async () => {
      const apiKey = config.GOOGLE_MAPS_API_KEY;
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          selectedAddress,
        )}&key=${apiKey}`,
      );
      if (response.ok) {
        const geocodeData = await response.json();
        if (geocodeData.results && geocodeData.results.length > 0) {
          const { geometry } = geocodeData.results[0];
          setSelectedAddressLatLng({
            lat: geometry.location.lat,
            lng: geometry.location.lng,
          });
        } else {
          console.error("No results found for this address");
        }
      } else {
        console.error(`Geocoding error: ${response.status}`);
      }
    };

    // Only geocode if address is set but latLng is NOT already set
    // (i.e., user typed an address manually, not selected from map)
    if (selectedAddress && !selectedAddressLatLng) {
      setIsCustomLocation(false);
      geocodeAddress();
    }
  }, [selectedAddress, selectedAddressLatLng]);

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.invalidateSize();
    }
  }, [
    isSidePanelOpen,
    showLayersAdmin,
    showCommentAdmin,
    showUpdateAdmin,
    showContribution,
    showPreferences,
  ]);

  const handleLogin = async () => {
    try {
      setIsModalOpen(true);
    } catch (error) {
      console.error("Error opening login form:", error);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false); // Close the modal
  };

  const [overlayLayers, setOverlayLayers] = useState([]);
  const [selectedLayer, setSelectedLayer] = React.useState(null);
  const [selectedBaseLayer, setSelectedBaseLayer] = React.useState(
    baseLayerOptions["JawgLight"],
  );

  useEffect(() => {
    if (project) {
      fetch(
        `${config.getApiUrl("fetchOverlayCity.php")}?projectid=${project.projectid
        }`,
      )
        .then((response) => response.json())
        .then((data) => {
          if (Array.isArray(data)) {
            const sortedData = data.sort((a, b) =>
              a?.name?.localeCompare(b?.name),
            );
            setOverlayLayers(sortedData);
          } else {
            console.error("Expected an array but got:", data);
          }
        })
        .catch((error) =>
          console.error("Error fetching overlay layers: ", error),
        );
    } else {
      fetch(`${config.getApiUrl("fetchOverlay.php")}`)
        .then((response) => response.json())
        .then((data) => {
          if (Array.isArray(data)) {
            const sortedData = data.sort((a, b) =>
              a?.name?.localeCompare(b?.name),
            );
            setOverlayLayers(sortedData);
          } else {
            console.error("Expected an array but got:", data);
          }
        })
        .catch((error) =>
          console.error("Error fetching overlay layers: ", error),
        );
    }
  }, [project]);

  const getGridSize = (
    isSidePanelOpen,
    showPreferences,
    showLayersAdmin,
    showCommentAdmin,
    showContributionOpen,
  ) => {
    // When side panel is fullscreen, hide the map completely

    if (isSidePanelOpen && isSidePanelFullscreen)
      return { xs: 0, sm: 0, md: 0 };
    if (isSidePanelOpen) return { xs: 0, sm: 6, md: 8 };
    /* Wizard is position:fixed over the map — keep map full width */
    if (showPreferences) return { xs: 0, sm: 6, md: 8 };
    if (showContributionOpen) return { xs: 0, sm: 6, md: 8 };
    if (showLayersAdmin) return { xs: 0, sm: 0, md: 0 };
    if (showCommentAdmin) return { xs: 0, sm: 0, md: 0 };
    if (showUpdateAdmin) return { xs: 0, sm: 0, md: 0 };
    return { xs: 12, sm: 12, md: 12 }; // Default case
  };
  const handleMarkerClick = async (clickedMarkerIds) => {
    setPolygonData(null);
    setLineData(null);
    setSelectedPolylineId(null);
    setEmptyEraPopup(null);

    clearBrowseTourIfActive();

    const clickedLocation = locations.find(
      (loc) => loc.ids.join("-") === clickedMarkerIds.join("-"),
    );
    if (!clickedLocation) return;

    const { loc_lat: clickedLat, loc_lng: clickedLng } = clickedLocation;
    const radius = 0.01;

    const nearbyLocations = locations.filter((loc) => {
      const distance = getDistanceFromLatLng(
        clickedLat,
        clickedLng,
        loc.loc_lat,
        loc.loc_lng,
      );
      return distance <= radius;
    });
    const nearbyMarkerIds = nearbyLocations.map((loc) => loc.ids);

    const currentDecade = Math.floor(year / 10) * 10;
    const hasContentForEra = nearbyLocations.some((loc) =>
      loc.years.some((y) => Math.floor(y / 10) * 10 === currentDecade),
    );

    if (!hasContentForEra) {
      // Clicking an out-of-era (hollow) marker shows the forward/back decade
      // popup. It must NOT silently jump the timeline.
      //
      // History, so this does not flip a third time: f737b1e ("Add hover-label
      // pins (PR #3) with closest-decade jump") replaced this popup with an
      // automatic setYear(nearestDecade). The stated reasoning was that the
      // hover label already told the user which decade they would land on — so
      // the picker was redundant. That reasoning depended on the hover label
      // being correct, and it was not: the label showed each location's
      // present-day title regardless of the selected decade (see
      // getMarkerHoverLabel / titles_by_year). The client asked for the popup
      // back in their own words: "a popup should appear with the text we
      // created which points the user either forward in time or backward (or
      // both) depending on which eras information has been entered about."
      //
      // PR #3's hover labels are KEPT. Only the silent jump is reverted.
      const allDecades = new Set();
      nearbyLocations.forEach((loc) =>
        loc.years.forEach((y) => {
          const d = Math.floor(Number(y) / 10) * 10;
          if (Number.isFinite(d)) allDecades.add(d);
        }),
      );
      if (allDecades.size === 0) {
        // Pathological: marker with no usable years/decades. Bail.
        return;
      }

      setSelectedMarker(clickedMarkerIds);
      setSelectedAddressLatLng({ lat: clickedLat, lng: clickedLng });
      setEmptyEraPopup({
        lat: clickedLat,
        lng: clickedLng,
        availableYears: Array.from(allDecades).sort((a, b) => a - b),
        markerIds: nearbyMarkerIds,
      });
      return;
    }

    setSelectedMarkerIds(nearbyMarkerIds);
    setSelectedMarker(clickedMarkerIds);
    setSelectedAddressLatLng({ lat: clickedLat, lng: clickedLng });
    setIsSidePanelOpen(true);
  };
  const activatePolygonDrawing = () => {
    setDrawingPoints([]);
    setPolygonCoordinates([]);
    setLineCoordinates([]);
    setIsDrawingLine(false);
    setSelectionType(SHAPE_TYPE.POLYGON);
    setActiveTool(SHAPE_TYPE.POLYGON);
    setIsDrawingPolygon(true);
  };

  const activateLineDrawing = () => {
    setDrawingPoints([]);
    setPolygonCoordinates([]);
    setLineCoordinates([]);
    setIsDrawingPolygon(false);
    setSelectionType(SHAPE_TYPE.LINE);
    setActiveTool(SHAPE_TYPE.LINE);
    setIsDrawingLine(true);
    lastLineClickRef.current = { time: 0, latlng: null };
  };

  useEffect(() => {
    if (!isDrawingPolygon) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setDrawingPoints([]);
        setPolygonCoordinates([]);
        setIsDrawingPolygon(false);
        setSelectionType(SHAPE_TYPE.MARKER);
        setActiveTool("point");
      } else if ((e.ctrlKey || e.metaKey) && e.key === "z") {
        e.preventDefault();
        setDrawingPoints((prev) => prev.length > 0 ? prev.slice(0, -1) : prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isDrawingPolygon]);

  // Same Esc / Ctrl-Z affordances as polygon drawing, scoped to line mode.
  useEffect(() => {
    if (!isDrawingLine) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setDrawingPoints([]);
        setLineCoordinates([]);
        setIsDrawingLine(false);
        setSelectionType(SHAPE_TYPE.MARKER);
        setActiveTool("point");
      } else if ((e.ctrlKey || e.metaKey) && e.key === "z") {
        e.preventDefault();
        setDrawingPoints((prev) => prev.length > 0 ? prev.slice(0, -1) : prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isDrawingLine]);

  const handlePolygonClick = (polygon, clickLatLng) => {
    clearBrowseTourIfActive();

    let resolvedPolygon = polygon;

    if (clickLatLng) {
      const candidates = polygons
        .map((p) => ({
          ...p,
          normalizedCoords: normalizePolygonCoordinates(p.coordinates),
        }))
        .filter((p) => p.normalizedCoords.length >= 3)
        .filter((p) => isPointInPolygon(clickLatLng, p.normalizedCoords));

      if (candidates.length > 0) {
        const smallest = candidates.reduce((best, p) => {
          const area = getPolygonArea(p.normalizedCoords);
          if (!best) return { ...p, area };
          return area < best.area ? { ...p, area } : best;
        }, null);

        if (smallest) {
          resolvedPolygon = polygons.find((p) => p.id === smallest.id) || polygon;
        }
      }
    }

    const { id } = resolvedPolygon;
    const coordinates = normalizePolygonCoordinates(resolvedPolygon.coordinates);

    if (!coordinates || coordinates.length < 3) return;

    setEmptyEraPopup(null);

    const currentDecade = Math.floor(year / 10) * 10;
    const currentCoords = JSON.stringify(resolvedPolygon.coordinates);

    const matchForDecade = polygons.find(
      (p) =>
        JSON.stringify(p.coordinates) === currentCoords &&
        Math.floor(Number(p.year) / 10) * 10 === currentDecade,
    );

    if (!matchForDecade) {
      const allDecades = new Set();
      polygons.forEach((p) => {
        if (JSON.stringify(p.coordinates) === currentCoords) {
          allDecades.add(Math.floor(Number(p.year) / 10) * 10);
        }
      });

      const centroid = coordinates.reduce(
        (acc, [lat, lng]) => ({ lat: acc.lat + lat / coordinates.length, lng: acc.lng + lng / coordinates.length }),
        { lat: 0, lng: 0 },
      );

      setIsSidePanelOpen(false);
      setSelectedAddressLatLng(null);
      setSelectedAddress(null);
      setEmptyEraPopup({
        lat: centroid.lat,
        lng: centroid.lng,
        availableYears: Array.from(allDecades).sort((a, b) => a - b),
        markerIds: [],
        polygonInfo: { id, coordinates, rawCoordinates: resolvedPolygon.coordinates },
      });
      return;
    }

    const bounds = L.latLngBounds(coordinates);

    const markerIdsInPolygon = locations
      .filter(
        (loc) =>
          typeof loc.loc_lat === "number" &&
          typeof loc.loc_lng === "number" &&
          bounds.contains([loc.loc_lat, loc.loc_lng]),
      )
      .map((loc) => loc.ids)
      .flat();

    setSelectedMarkerIds(markerIdsInPolygon);
    setPolygonData({ id, coordinates });
    setSelectedPolygonId(id);
    setPolygonCoordinates(coordinates);
    setSelectedAddressLatLng(null);
    setSelectedAddress(null);
    setIsSidePanelOpen(true);
    setSelectionType(SHAPE_TYPE.POLYGON);
  };

  const handlePolygonDrawClick = (latlng) => {
    const nextPoint = [latlng.lat, latlng.lng];
    if (drawingPoints.length >= 3) {
      const [firstLat, firstLng] = drawingPoints[0];
      const distanceToStart = getDistanceFromLatLng(
        latlng.lat,
        latlng.lng,
        firstLat,
        firstLng,
      );
      const currentZoom = mapRef.current ? mapRef.current.getZoom() : zoom;
      const metersPerPixel = 156543.03392 * Math.cos(latlng.lat * Math.PI / 180) / Math.pow(2, currentZoom);
      const thresholdKm = (20 * metersPerPixel) / 1000;
      if (distanceToStart <= thresholdKm) {
        setPolygonCoordinates([...drawingPoints, nextPoint]);
        setDrawingPoints([]);
        setIsDrawingPolygon(false);
        if (!showWizard) {
          setShowContribution(true);
          setIsSidePanelOpen(false);
        }
        return;
      }
    }
    setDrawingPoints((prevPoints) => [...prevPoints, nextPoint]);
  };

  // Line drawing: each click adds a vertex; double-click finishes the path
  // (handled by the dblclick branch in MapEventsHandler). To avoid the
  // dblclick's two underlying clicks both adding duplicate vertices right
  // before finish, we suppress a click that lands within ~350ms and ~1m of
  // the previous click.
  const handleLineDrawClick = (latlng) => {
    const now = Date.now();
    const last = lastLineClickRef.current;
    if (last?.latlng) {
      const dist = getDistanceFromLatLng(
        latlng.lat,
        latlng.lng,
        last.latlng.lat,
        last.latlng.lng,
      );
      if (now - last.time < 350 && dist < 0.001) {
        lastLineClickRef.current = { time: now, latlng };
        return;
      }
    }
    lastLineClickRef.current = { time: now, latlng };
    setDrawingPoints((prevPoints) => [...prevPoints, [latlng.lat, latlng.lng]]);
  };

  // Finalize the in-progress line. We require ≥2 vertices; if the dblclick
  // landed somewhere new (e.g. user dblclicked while only 1 point existed)
  // and we don't have at least one prior vertex, we just bail and let the
  // user keep clicking.
  const finishLineDrawing = (latlng) => {
    setDrawingPoints((prev) => {
      let pts = prev;
      // If the user dblclicked far from the last vertex, treat it as
      // "place the final point here, then finish". Two of the dblclick's
      // clicks were already merged by handleLineDrawClick — anything that
      // lands here must be a fresh vertex worth committing.
      if (latlng && pts.length > 0) {
        const lastPt = pts[pts.length - 1];
        const dist = getDistanceFromLatLng(
          latlng.lat,
          latlng.lng,
          lastPt[0],
          lastPt[1],
        );
        if (dist >= 0.001) {
          pts = [...pts, [latlng.lat, latlng.lng]];
        }
      }
      if (pts.length < 2) {
        // Not enough to be a line yet; keep drawing.
        return pts;
      }
      setLineCoordinates(pts);
      setIsDrawingLine(false);
      lastLineClickRef.current = { time: 0, latlng: null };
      if (!showWizard) {
        setShowContribution(true);
        setIsSidePanelOpen(false);
      }
      return [];
    });
  };

  const handlePolylineClick = (polyline) => {
    clearBrowseTourIfActive();
    const { id } = polyline;
    const coordinates = normalizePolygonCoordinates(polyline.coordinates);
    if (!coordinates || coordinates.length < 2) return;

    setEmptyEraPopup(null);
    setSelectedAddressLatLng(null);
    setSelectedAddress(null);
    setLineData({ id, coordinates });
    setSelectedPolylineId(id);
    setLineCoordinates(coordinates);
    setSelectedMarkerIds([]);
    setPolygonData(null);
    setSelectedPolygonId(null);
    setIsSidePanelOpen(true);
    setSelectionType(SHAPE_TYPE.LINE);
  };

  const normalizePolygonCoordinates = (coordinates) => {
    if (!coordinates) return [];
    if (typeof coordinates === "string") {
      try {
        const parsed = JSON.parse(coordinates);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch (error) {
        return [];
      }
    }
    if (!Array.isArray(coordinates)) return [];
    return coordinates
      .map((point) => {
        if (!Array.isArray(point) || point.length < 2) return null;
        const lat = Number(point[0]);
        const lng = Number(point[1]);
        if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
        return [lat, lng];
      })
      .filter(Boolean);
  };

  const parseLatLngFromLocId = (locId, row) => {
    if (typeof row?.loc_lat === "number" && typeof row?.loc_lng === "number") {
      return { lat: row.loc_lat, lng: row.loc_lng };
    }
    if (typeof locId !== "string") return null;
    if (locId.trim().startsWith("[")) return null;
    const parts = locId.split(",");
    if (parts.length < 2) return null;
    const lat = Number(parts[0]);
    const lng = Number(parts[1]);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    return { lat, lng };
  };

  const handleTableRowSelect = (row) => {
    if (!row) return;
    const nextYear = Number(row.year);
    if (!Number.isNaN(nextYear)) {
      setYear(nextYear);
    }

    setShowContribution(false);
    setAddStoryMode(false);
    setIsEditing(false);
    setIsSidePanelOpen(true);
    setIsSidePanelFullscreen(false);

    if (row.shape_type === SHAPE_TYPE.POLYGON) {
      const coordinates = normalizePolygonCoordinates(row.loc_id);
      setSelectionType(SHAPE_TYPE.POLYGON);
      setPolygonData({ id: row.id, coordinates });
      setPolygonCoordinates(coordinates);
      setSelectedPolygonId(row.id);
      setLineData(null);
      setLineCoordinates([]);
      setSelectedPolylineId(null);
      setSelectedMarkerIds([]);
      setSelectedAddressLatLng(null);
      return;
    }

    if (row.shape_type === SHAPE_TYPE.LINE) {
      const coordinates = normalizePolygonCoordinates(row.loc_id);
      setSelectionType(SHAPE_TYPE.LINE);
      setLineData({ id: row.id, coordinates });
      setLineCoordinates(coordinates);
      setSelectedPolylineId(row.id);
      setPolygonData(null);
      setPolygonCoordinates([]);
      setSelectedPolygonId(null);
      setSelectedMarkerIds([]);
      setSelectedAddressLatLng(null);
      return;
    }

    const latLng = parseLatLngFromLocId(row.loc_id, row);
    setSelectionType(SHAPE_TYPE.MARKER);
    setPolygonData(null);
    setLineData(null);
    setPolygonCoordinates([]);
    setLineCoordinates([]);
    setSelectedPolygonId(null);
    setSelectedPolylineId(null);
    setSelectedMarkerIds([row.id]);
    setSelectedMarker([row.id]);
    if (latLng) {
      setSelectedAddressLatLng(latLng);
    }
  };

  const getPolygonArea = (coordinates) => {
    if (!coordinates || coordinates.length < 3) return 0;
    let area = 0;
    for (
      let i = 0, j = coordinates.length - 1;
      i < coordinates.length;
      j = i++
    ) {
      const [lat1, lng1] = coordinates[j];
      const [lat2, lng2] = coordinates[i];
      area += lng1 * lat2 - lng2 * lat1;
    }
    return Math.abs(area / 2);
  };

  const isPointInPolygon = (point, coordinates) => {
    if (!coordinates || coordinates.length < 3) return false;
    const x = point.lng;
    const y = point.lat;
    let inside = false;
    for (
      let i = 0, j = coordinates.length - 1;
      i < coordinates.length;
      j = i++
    ) {
      const xi = coordinates[i][1];
      const yi = coordinates[i][0];
      const xj = coordinates[j][1];
      const yj = coordinates[j][0];
      const intersects =
        (yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi + 0.0) + xi;
      if (intersects) inside = !inside;
    }
    return inside;
  };

  const getHoveredPolygonId = (latlng) => {
    const candidatePolygons = polygons
      .filter((polygon) => {
        const polygonYear = Number(polygon.year);
        return polygonYear === Number(year);
      })
      .map((polygon) => ({
        id: polygon.id,
        coordinates: normalizePolygonCoordinates(polygon.coordinates),
      }))
      .filter((polygon) => polygon.coordinates.length >= 3)
      .filter((polygon) => isPointInPolygon(latlng, polygon.coordinates));

    if (candidatePolygons.length === 0) return null;

    const smallestPolygon = candidatePolygons.reduce((smallest, polygon) => {
      const area = getPolygonArea(polygon.coordinates);
      if (!smallest) return { ...polygon, area };
      return area < smallest.area ? { ...polygon, area } : smallest;
    }, null);

    return smallestPolygon ? smallestPolygon.id : null;
  };

  // Function to scroll the container left
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft -= 150; // Adjust the value as needed
    }
  };

  // Function to scroll the container right
  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft += 150; // Adjust the value as needed
    }
  };

  const addLocation = () => {
    setSelectedAddress("");
    setSelectedAddressLatLng(null);
    setEmptyEraPopup(null);
    setShowWizard(true);
    setShowContribution(false);
    setIsSidePanelOpen(false);
    setStoryVisibility(getDefaultVisibility());
  };

  const switchWizardToLegacy = () => {
    setShowWizard(false);
    setShowContribution(true);
  };

  const closeWizard = () => {
    setShowWizard(false);
    setSelectMode(false);
    setIsDrawingPolygon(false);
    setDrawingPoints([]);
    setPolygonCoordinates([]);
    setSelectionType(SHAPE_TYPE.MARKER);
    setActiveTool("point");
    setSelectedAddress("");
    setSelectedAddressLatLng(null);
  };

  // DISABLED: handleToolChange - uncomment when re-enabling map toolbox
  // eslint-disable-next-line no-unused-vars
  const _handleToolChange = (tool) => {
    setActiveTool(tool);
    if (tool === SHAPE_TYPE.POLYGON) {
      setSelectionType(SHAPE_TYPE.POLYGON);
      setIsDrawingPolygon(true);
      setSelectMode(false);
      return;
    }
    setSelectionType(SHAPE_TYPE.MARKER);
    setIsDrawingPolygon(false);
    setDrawingPoints([]);
    setPolygonCoordinates([]);
  };

  useEffect(() => {
    if (!showRegions) {
      setHoveredPolygonId(null);
      hoveredPolygonIdRef.current = null;
    }
  }, [showRegions]);

  // True when the user is actively placing a marker via "Click a spot on the
  // map" OR drawing a polygon. While either is true, existing markers and
  // polygons should be visually present but completely non-interactive so
  // the user's click can't accidentally land on a sibling shape and open
  // its side panel / select it instead of placing a point or adding a
  // polygon vertex. Mirrors the same pattern used for `selectMode` alone in
  // the previous SP.POLY click-on-map fix.
  const isDrawingOrPlacingShape =
    (selectMode && activeTool === "point") || isDrawingPolygon || isDrawingLine;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100vw",
      }}
    >
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="login-modal"
        aria-describedby="login-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "55%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            pt: 0,
            pb: 4,
            pr: 4,
            pl: 4,
            borderRadius: 2,
          }}
        >
          <AuthForm onSuccess={handleCloseModal} />
        </Box>
      </Modal>

      {/* Map Selection Suggestions Dialog*/}
      <AddressSuggestionDialog
        open={showSuggestionPopup}
        onClose={() => setShowSuggestionPopup(false)}
        suggestedAddress={suggestedAddress}
        suggestions={suggestedAddresses}
        selectedSuggestion={selectedSuggestion}
        onSelectSuggestion={handleSelectSuggestion}
        onKeepCustom={handleKeepCustom}
        onAcceptSuggestion={handleAcceptSuggestion}
        isCustomSelect={customSelectMode}
      />
      {/* App header.
       *
       * Layout rule (see DESIGN.md §3.2): NEVER combine a fixed `height` with
       * `overflow: hidden` on this container — it caused the 2026-06
       * client-blocking regression where 224px of content was crammed into
       * 151px and clipped from view. Use `minHeight` and let the content
       * determine the final height. The previous fixed-height values are
       * preserved as minHeight floors so the header doesn't collapse below
       * the design target when content fits.
       *
       * 2026-06-08 topbar coherence pass: dropped the minHeight floor from
       * 151px → 120px. The 151px floor was sized for a 100px logo + 2-line
       * title; now the logo is 64px so the natural content height is ~120px.
       * Keeping the old floor was forcing 30px of dead whitespace that pushed
       * the map down for no visual gain. 120px still accommodates the
       * tallest sibling column (the 92px search/timeline card + 16px
       * paddingBlock) so the header never collapses below its design floor.
       */}
      <Box
        sx={{
          display: isSidePanelFullscreen ? "none" : "flex",
          // 2026-06-08 topbar coherence pass 3: flex-start was leaving
          // the right column (welcome line + DisplayControlsBar + avatar)
          // glued to the very top while the middle search card sat lower,
          // so the avatar/login button visually orphaned from the bar.
          // Centering aligns every column to the same vertical baseline,
          // restoring the "grouped right-rail" reading the client expects.
          alignItems: "center",
          width: "100%",
          position: "relative",
          backgroundColor: ws.surface.header,
          minHeight: { xs: "auto", md: "120px" },
          paddingInline: ws.space[5],
          paddingBlock: { xs: ws.space[3], md: ws.space[2] },
          boxSizing: "border-box",
        }}
      >
        {/* 2026-06-08 topbar coherence pass 3: container-level alignItems
            "center" so each Grid item (logo column, center search card,
            right-rail action group) vertically centers against the row's
            tallest item (the 92px search card). Without this, MUI's
            default `stretch` makes columns equal-height and their content
            top-aligns within — exactly what produced the "share/avatar
            floats above the search bar" gap the client flagged. */}
        <Grid container alignItems="center">
          {/* Project identity column.
           *
           * Layout rule (see DESIGN.md §3.2): the project title is rendered
           * INLINE underneath the logo using normal flow. The previous build
           * positioned the title with `position: absolute; top: 55px;
           * left: 28px; width: 122px`, which forced "City of Madison 250th
           * Project" to wrap to four narrow lines (one word per line) and
           * pinned the title to fixed coordinates that ignored the column's
           * real width.
           *
           * 2026-06-08 topbar coherence pass 3: tightened from md=3 (25%)
           * to md=2 (~16.7%) so the center search/timeline card can take
           * its target ~67% width (md=8). The 64×77 logo + project title
           * fits comfortably at ~170px on a 1024px viewport — "City of
           * Madison 250th Project" wraps to two lines naturally, which
           * was already the design's intent.
           */}
          <Grid item xs={6} sm={6} md={2} sx={{ order: { xs: 1, md: 1 } }}>
            <Box
              onClick={() => {
                resetToDefaultState();
                fetchLocations();
              }}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                cursor: "pointer",
                maxWidth: "240px",
              }}
            >
              {/*
                2026-06-08 topbar coherence pass 4: respect the SVG's native
                128×46 aspect ratio (~2.78:1). The previous 77×64 box squashed
                MAP-250.svg into a too-tall container — preserveAspectRatio
                fit the glyphs to the narrow 77px width (rendered ~28px tall)
                and centered them inside the 64px box, producing ~18px of
                empty padding above AND below the visible MAP250 letters.
                That phantom padding is exactly what the client read as
                "MAP250 is too far from the project name and the logo looks
                small." Switching to width:110 / height:auto draws MAP250 at
                its native proportions (~40px effective height) and removes
                the internal padding entirely. Net column height: ~40 (logo)
                + 2 (gap) + 30 (2-line title) = 72px content — still well
                under the 120px AppHeader minHeight, so the bar's overall
                size doesn't change. display:block kills the inline-image
                baseline whitespace under the img.
              */}
              <img
                src="./MAP-250.svg"
                alt="MAP250"
                style={{
                  display: "block",
                  width: "110px",
                  height: "auto",
                }}
              />

              <Typography
                sx={{
                  marginTop: ws.space["0_5"],
                  lineHeight: ws.leading.tight,
                  fontWeight: ws.weight.bold,
                  fontSize: ws.text.md,
                  color: ws.color.body,
                  maxWidth: "100%",
                  wordBreak: "normal",
                  overflowWrap: "normal",
                }}
              >
                {project?.project_name}
              </Typography>
            </Box>
          </Grid>
          {/* 2026-06-08 topbar coherence pass 3: center column widened
              from md=6 (50%) to md=8 (~67%). The client read the old
              50% allocation as "the search bar feels cramped" — the
              search input got crowded by the surrounding columns even
              though there was empty space on the sides. md=8 gives the
              SearchBar / Timeline / Map preferences trio the dominant
              visual weight the top bar's information hierarchy demands
              (search + time is the primary action surface). */}
          <Grid
            item
            xs={12}
            sm={12}
            md={8}
            sx={{
              order: { xs: 3, md: 2 },
              position: "relative",
            }}
          >
            {/* Slider wrap */}
            <Grid
              container
              spacing={1}
              alignItems="center"
              sx={{
                width: "100%",
                backgroundColor: "#fff",
                height: { xs: "auto", md: "92px" },
                borderRadius: "0px",
                paddingLeft: "0px",
                paddingRight: "0px",
                paddingTop: "0px",
                paddingBottom: "0px",
                margin: 0,
                marginTop: { xs: "10px", md: "0" },
              }}
            >
              <Grid
                item
                xs={7}
                sm={7}
                md={3}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  paddingRight: "8px",
                  order: { xs: 1, md: 1 },
                  paddingTop: "10px !important",
                  transition: "background-color 0.3s ease",
                  "&:focus-within": {
                    backgroundColor: "var(--theme-bg-clr)",
                  },
                }}
              >
                {/* search section address keyword */}
                <Box sx={{ width: "100%" }}>
                  <SearchBar
                    ref={searchBarRef}
                    isAdmin={adminOrMod()}
                    onSearchSelect={handleSearchSelect}
                    clearSidePanel={clearSidePanel}
                    user={user}
                    project={project}
                    year={year}
                    mapBounds={
                      mapRef.current ? mapRef.current.getBounds() : null
                    }
                    mapCenter={
                      mapRef.current
                        ? mapRef.current.getCenter()
                        : { lat: mapCenter[0], lng: mapCenter[1] }
                    }
                    mapRef={mapRef}
                  />
                </Box>
                {/* search section address keyword End */}
              </Grid>

              <Grid
                item
                xs={12}
                sm={12}
                md={6}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  justifyContent: "center",
                  justifyItems: "center",
                  position: "relative",
                  zIndex: 100,
                  borderLeft: "1px solid #DDDAD4",
                  borderRight: "1px solid #DDDAD4",
                  order: { xs: 3, md: 2 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <IconButton onClick={scrollLeft}>
                    <NavigateBefore />
                  </IconButton>
                  <div
                    ref={(el) => {
                      scrollContainerRef.current = el;
                      timelineSliderRef.current = el;
                    }}
                    style={{
                      overflowX: "auto",
                      overflowY: "hidden",
                      width: "85%",
                      whiteSpace: "nowrap",
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                    }}
                  >
                    <Slider
                      className="slider__time"
                      aria-label="Time-Slider"
                      defaultValue={1950}
                      getAriaValueText={valuetext}
                      min={1760}
                      max={2030}
                      step={10}
                      value={year}
                      marks={marks}
                      sx={{
                        width: "1400px",
                        display: "inline-block",
                        paddingRight: "30px",
                        paddingLeft: "30px",
                        marginLeft: "50px",
                        marginRight: "50px",
                      }}
                      onChange={(e, newValue) => {
                        setYear(newValue);
                      }}
                    />
                  </div>
                  <IconButton onClick={scrollRight}>
                    <NavigateNext />
                  </IconButton>
                </Box>
              </Grid>

              <Grid
                item
                xs={5}
                sm={5}
                md={3}
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  paddingRight: "16px",
                  order: { xs: 2, md: 3 },
                  cursor: "pointer",
                  transition: "background-color 0.3s ease",
                  "&:has(.txt-map[aria-expanded='true'])": {
                    backgroundColor: "var(--theme-bg-clr)",
                  },
                }}
              >
                <div>
                  <Typography
                    variant="h5"
                    sx={{
                      color: "var(--theme-color)",
                      fontSize: "12px",
                      textTransform: "uppercase",
                    }}
                  >
                    Map
                  </Typography>

                  <Tooltip
                    title={
                      (selectedBaseLayer
                        ? selectedBaseLayer?.name
                        : "Base Map") +
                      " / " +
                      (selectedLayer ? selectedLayer?.name : "No Overlay")
                    }
                  >
                    <Typography
                      variant="h6"
                      className="txt-map"
                      sx={{
                        color: "var(--theme-color)",
                        fontSize: "13px",
                        fontWeight: "700",
                        textTransform: "uppercase",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      id="basic-button"
                      aria-controls="basic-menu"
                      aria-haspopup="true"
                      aria-expanded={isMenuOpen ? "true" : undefined}
                      onClick={handleMenuOpen}
                    >
                      {(selectedBaseLayer
                        ? selectedBaseLayer?.name
                        : "Base Map") +
                        " / " +
                        (selectedLayer ? selectedLayer?.name : "No Overlay")}
                    </Typography>
                  </Tooltip>

                  <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={isMenuOpen}
                    onClose={handleMenuClose}
                    MenuListProps={{
                      "aria-labelledby": "basic-button",
                    }}
                    PaperProps={{
                      sx: {
                        width: {
                          xs: "90vw", // for small screens (mobile)
                          sm: "90vw", // for tablets
                          md: "700px", // for desktop
                        },
                        maxWidth: "100%", // ensures it never overflows
                        mt: 3,
                        "& .MuiPopover-paper": {
                          boxShadow: "unset !important",
                          padding: "unset !important",
                          color: "var(--theme-color) !important",
                        },
                        "& .MuiMenu-list": {
                          padding: "0 !important",
                        },
                      },
                    }}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    <Box>
                      <ErrorBoundary>
                        <Preferences
                          setShowPreferences={setShowPreferences}
                          overlayLayers={overlayLayers}
                          baseMapLayer={baseLayerOptions["JawgLight"]}
                          selectedBaseLayer={selectedBaseLayer}
                          setSelectedBaseLayer={setSelectedBaseLayer}
                          selectedOverlayLayer={selectedLayer}
                          setSelectedOverlayLayer={setSelectedLayer}
                        />
                      </ErrorBoundary>
                    </Box>
                  </Menu>
                </div>
                {/* Menu section End*/}
              </Grid>
            </Grid>
            {/* Slider wrap End*/}

            {/* DISABLED: Show Regions, Vetted Sites, and Join Group buttons - uncomment to re-enable
            {!isExhibitionMode && (
              <>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setShowOnlyVetted((prev) => !prev)}
                  sx={{
                    backgroundColor: showOnlyVetted
                      ? "var(--theme-color) !important"
                      : "#fff !important",
                    color: showOnlyVetted
                      ? "#fff !important"
                      : "var(--theme-color) !important",
                    fontWeight: "700 !important",
                    textTransform: "unset !important",
                    lineHeight: "unset",
                    minHeight: "24px",
                    padding: "2px 10px",
                    fontSize: "0.7rem",
                    position: "absolute",
                    top: "95px",
                    left: "40%",
                  }}
                >
                  Show Only Vetted Sites
                </Button>
                {isMapGroupMember ? (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setShowOnlyMapGroup((prev) => !prev)}
                    sx={{
                      backgroundColor: showOnlyMapGroup
                        ? "var(--theme-color) !important"
                        : "#fff !important",
                      color: showOnlyMapGroup
                        ? "#fff !important"
                        : "var(--theme-color) !important",
                      fontWeight: "700 !important",
                      textTransform: "unset !important",
                      lineHeight: "unset",
                      minHeight: "24px",
                      padding: "2px 10px",
                      fontSize: "0.7rem",
                      position: "absolute",
                      top: "95px",
                      left: "60%",
                    }}
                  >
                    Show Only Map Group Sites
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleJoinRequest}
                    disabled={joinRequestSent || joinRequestSubmitting}
                    sx={{
                      backgroundColor:
                        joinRequestSent || joinRequestSubmitting
                          ? "#e0e0e0 !important"
                          : "#fff !important",
                      color:
                        joinRequestSent || joinRequestSubmitting
                          ? "#7a7a7a !important"
                          : "var(--theme-color) !important",
                      fontWeight: "700 !important",
                      textTransform: "unset !important",
                      lineHeight: "unset",
                      minHeight: "24px",
                      padding: "2px 10px",
                      fontSize: "0.7rem",
                      position: "absolute",
                      top: "95px",
                      left: "60%",
                    }}
                  >
                    Ask to join this group.
                  </Button>
                )}
                <Button
                  variant="contained"
                  onClick={() => setShowRegions((prev) => !prev)}
                  sx={{
                    backgroundColor: showRegions
                      ? "var(--theme-color) !important"
                      : "#fff !important",
                    color: showRegions
                      ? "#fff !important"
                      : "var(--theme-color) !important",
                    fontWeight: "700 !important",
                    textTransform: "unset !important",
                    lineHeight: "unset",
                    minHeight: "24px",
                    padding: "2px 10px",
                    fontSize: "0.7rem",
                    position: "absolute",
                    top: "95px",
                    left: "25%",
                  }}
                >
                  {showRegions ? "Hide Regions" : "Show Regions"}
                </Button>
              </>
            )}
            */}
          </Grid>
          {/* 2026-06-08 topbar coherence pass 3: right rail tightened
              from md=3 to md=2 so 2 + 8 + 2 = 12 columns balanced
              against the new wider middle. The welcome line + avatar
              cluster fits comfortably at ~170px; long emails already
              ellipsize via the Typography above and the avatar is a
              fixed 44×44 button. alignItems flipped from flex-start to
              center so the avatar/login button shares a vertical
              baseline with the welcome line + DisplayControlsBar
              stack — they read as a single "you are here" cluster
              instead of two top-pinned blocks with awkward whitespace
              below. */}
          <Grid item xs={6} sm={6} md={2} sx={{ order: { xs: 2, md: 3 } }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 1,
                minWidth: 0,
              }}
            >
              {loading ? (
                <Typography variant="body2" color="textSecondary">
                  Loading...
                </Typography>
              ) : user && !isExhibitionMode ? (
                <>
                  {/* Right-rail identity cluster (June 2026 client mockup):
                      row 1 = welcome line + avatar on one line; row 2 = a
                      labeled "Display Settings" text button beneath, which
                      opens the full-height Display settings drawer (Show My
                      Places, Type of Entry, Contributor, Date Added, Show
                      regions). */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 0.5,
                      minWidth: 0,
                      flex: "1 1 auto",
                    }}
                  >
                    {/* Row 1: welcome line + avatar, on one line. */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 0,
                        maxWidth: "100%",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: ws.color.body,
                          fontWeight: ws.weight.medium,
                          textAlign: "right",
                          // Long emails must truncate cleanly instead of
                          // forcing the welcome line to wrap and inflate the
                          // header. The full address is still available via
                          // the title attribute on hover.
                          maxWidth: "100%",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={user.displayName || user.email || ""}
                      >
                        Welcome, {user.displayName || user.email}
                      </Typography>

                      <IconButton
                        onClick={handleProfileMenuOpen}
                        sx={{ color: "#413A36", p: 0.5, flex: "0 0 auto" }}
                        aria-label="user menu"
                      >
                        {profile?.photo_url ? (
                          <Avatar
                            src={profile.photo_url}
                            alt={profile.display_name || user.email || "User"}
                            sx={{ width: 36, height: 36 }}
                          />
                        ) : (
                          <AccountCircleIcon sx={{ fontSize: 36 }} />
                        )}
                      </IconButton>
                    </Box>

                    {/* Row 2: Show My Places highlight toggle (client
                        re-clarification, July 2026). Pure ON/OFF highlight,
                        default OFF. OFF -> label "Show My Places" (the action
                        a click performs: reveal the black pins). ON -> label
                        "Stop Showing My Places" and the button fills dark to
                        signal the highlight is active. This NEVER hides any
                        place — it only adds/removes the big black pins on the
                        user's own sites. */}
                    <Button
                      onClick={() => {
                        const next = !highlightMyPlaces;
                        setHighlightMyPlaces(next);
                        // "Show my places" must actually reveal them: if a
                        // stale/drawer-set HIDE_MINE filter is hiding the
                        // user's own sites, there'd be nothing to highlight.
                        // Clear it back to ALL when turning the highlight on
                        // so the black pins always appear.
                        if (
                          next &&
                          showMyPlaces === SHOW_MY_PLACES_MODE.HIDE_MINE
                        ) {
                          setShowMyPlaces(SHOW_MY_PLACES_MODE.ALL);
                        }
                      }}
                      aria-pressed={highlightMyPlaces}
                      aria-label={
                        highlightMyPlaces
                          ? "Stop showing my places"
                          : "Show my places"
                      }
                      startIcon={<PersonOutlineIcon sx={{ fontSize: 18 }} />}
                      sx={{
                        textTransform: "none",
                        fontSize: 13,
                        fontWeight: 600,
                        lineHeight: 1.2,
                        py: "2px",
                        px: 1,
                        minWidth: 0,
                        whiteSpace: "nowrap",
                        color: highlightMyPlaces ? "#fff" : "#413A36",
                        backgroundColor: highlightMyPlaces
                          ? "#413A36"
                          : "transparent",
                        border: "1px solid #D9D4C6",
                        borderRadius: "8px",
                        "&:hover": {
                          backgroundColor: highlightMyPlaces
                            ? "#2c2825"
                            : "#EEE9D8",
                        },
                      }}
                    >
                      {highlightMyPlaces
                        ? "Stop Showing My Places"
                        : "Show My Places"}
                    </Button>
                  </Box>

                  <Menu
                    anchorEl={profileAnchorEl}
                    open={isProfileMenuOpen}
                    onClose={handleProfileMenuClose}
                    PaperProps={{
                      elevation: 3,
                      sx: { minWidth: 180, borderRadius: 2, mt: 1 },
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        handleProfileMenuClose();
                        navigate("/profile");
                      }}
                    >
                      <PersonOutlineIcon fontSize="small" sx={{ mr: 1 }} />
                      View profile
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        logout();
                        handleProfileMenuClose();
                      }}
                    >
                      <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                      Logout
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                !isExhibitionMode && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleLogin}
                    startIcon={<LoginIcon />}
                    sx={{
                      backgroundColor: "#fff !important",
                      color: "var(--theme-color) !important",
                      fontWeight: "900 !important",
                      textTransform: "unset !important",
                      lineHeight: "unset",
                    }}
                  >
                    Login
                  </Button>
                )
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
      {showTableView ? (
        <Grid
          container
          sx={{ height: "100%", overflow: "hidden", boxSizing: "border-box" }}
        >
          <Grid
            item
            xs={12}
            sm={isSidePanelOpen ? 6 : 12}
            md={isSidePanelOpen ? 8 : 12}
            sx={{ height: "100%" }}
          >
            <LocationPostsTable
              project={project}
              user={user}
              onBackToMap={() => setShowTableView(false)}
              onRowSelect={handleTableRowSelect}
              highlightedSubmissionId={
                selectedPolygonId != null
                  ? selectedPolygonId
                  : selectedMarkerIds?.length
                    ? Array.isArray(selectedMarkerIds[0])
                      ? selectedMarkerIds[0][0]
                      : selectedMarkerIds[0]
                    : null
              }
            />
          </Grid>
          {isSidePanelOpen && (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              sx={{
                backgroundColor: "#f5f5f5",
                overflowY: "auto",
                position: "relative",
                height: "100%",
              }}
            >
              <SidePanelComponent
                isSidePanelOpen={isSidePanelOpen}
                setIsSidePanelOpen={setIsSidePanelOpen}
                isSidePanelFullscreen={isSidePanelFullscreen}
                setIsSidePanelFullscreen={setIsSidePanelFullscreen}
                year={year}
                setYear={setYear}
                setSelectedMarker={setSelectedMarker}
                selectedAddressLatLng={selectedAddressLatLng}
                setSelectedAddressLatLng={setSelectedAddressLatLng}
                setShowContribution={setShowContribution}
                setSelectedAddress={setSelectedAddress}
                setStoryVisibility={setStoryVisibility}
                markerIds={selectedMarkerIds}
                refreshData={fetchLocations}
                setIsEditing={setIsEditing}
                isEditing={isEditing}
                addStoryMode={addStoryMode}
                setAddStoryMode={setAddStoryMode}
                polygonData={polygonData}
                lineData={lineData}
                selectedAddress={selectedAddress}
                locationData={locations}
                isExhibitionMode={isExhibitionMode}
                setSelectionType={setSelectionType}
                setPolygonCoordinates={setPolygonCoordinates}
                setLineCoordinates={setLineCoordinates}
                polygons={polygons}
                polylines={polylines}
                pendingFocusShapeId={pendingFocusShapeId}
                onPendingFocusConsumed={handlePendingFocusConsumed}
              />
            </Grid>
          )}
        </Grid>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            minHeight: 0,
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          {/* Top bar (FilterBar) spans the full screen width and STAYS
              full-width regardless of the side panel — it lives ABOVE the
              map + side-panel row, not inside the map column, so opening
              the side panel no longer shrinks it. Hidden only when the
              side panel is fullscreen (the map isn't visible then). */}
          {!(isSidePanelOpen && isSidePanelFullscreen) && (
            <FilterBar
              onOpenContributors={() => setContributorsDirectoryOpen(true)}
            />
          )}
          <ContributorsDirectory
            open={contributorsDirectoryOpen}
            onClose={closeContributorsDirectory}
          />
          <Grid
            container
            sx={{
              flex: "1 1 auto",
              minHeight: 0,
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            {/* Map Section */}
            <Grid
              item
              {...getGridSize(
              isSidePanelOpen,
              showPreferences,
              showLayersAdmin,
              showCommentAdmin,
              showContribution,
            )}
            sx={{
              position: "relative",
              height: "100%",
              /* xs/sm/md=0 does not hide the grid item; abspos controls would still show over fullscreen panel */
              ...(isSidePanelOpen &&
                isSidePanelFullscreen && {
                  display: "none",
                }),
            }}
          >
            {/* "Add a New Place" used to live here at top:10, right:60.
                It now renders inside the map-area sibling of FilterBar
                (see flex-column wrapper below) so it sits at the top
                of the visible map without overlapping the filter strip
                and follows the strip down when the strip grows. */}
            {/* Show/Hide Regions has moved into FilterBar → More Filters →
                Display Regions. The toggle there is the single source of truth
                and persists across sessions. */}
            {/* <Box
            sx={{ position: "absolute", bottom: 24, right: 20, zIndex: 1000 }}
          >
            <Button
              variant="contained"
              onClick={handleShowPopup}
              startIcon={<InfoIcon />}
            >
              About Map
            </Button>
          </Box> */}
            <Box
              sx={{
                position: "absolute",
                bottom: 24,
                right: 20,
                zIndex: 1000,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                alignItems: "flex-end",
              }}
            >
              {canViewTable() && !isExhibitionMode && (
                <Button
                  sx={{
                    backgroundColor: "#fff !important",
                    color: "var(--theme-color) !important",
                    fontWeight: "900 !important",
                    lineHeight: "unset",
                  }}
                  onClick={() => setShowTableView(true)}
                  variant="contained"
                >
                  View Table Mode
                </Button>
              )}
              {adminOrMod() && !isExhibitionMode && (
                <Button
                  sx={{
                    backgroundColor: "#fff !important",
                    color: "var(--theme-color) !important",
                    fontWeight: "900 !important",
                    lineHeight: "unset",
                  }}
                  aria-controls="admin-menu"
                  aria-haspopup="true"
                  onClick={() => navigate("/admin")}
                  variant="contained"
                >
                  Admin Console
                </Button>
              )}
            </Box>
            {/* <Box
            sx={{ position: "absolute", bottom: 75, right: 20, zIndex: 1000 }}
          >
            <Button
              variant="contained"
              onClick={() => setShowPreferences(!showPreferences)}
              startIcon={<LayersIcon />}
            >
              Map Layers
            </Button>
          </Box> */}
            <OnboardingPopup
              open={onboardingOpen}
              isExhibitionMode={isExhibitionMode}
              customTitle={isExhibitionMode ? project?.exhibition_title : undefined}
              customMessage={isExhibitionMode ? project?.exhibition_message : undefined}
              onBrowse={() => {
                setOnboardingOpen(false);
                setActiveTourFlow(TOUR_FLOWS.browse);
              }}
              onAddPlace={() => {
                setOnboardingOpen(false);
                setActiveTourFlow(TOUR_FLOWS.addPlace);
              }}
              onClose={() => setOnboardingOpen(false)}
              onDismissForever={() => {
                dismissOnboarding();
                setOnboardingOpen(false);
              }}
              onSignIn={() => {
                setOnboardingOpen(false);
                setIsModalOpen(true);
              }}
              onCreateAccount={() => {
                setOnboardingOpen(false);
                setIsModalOpen(true);
              }}
            />


            {(() => {
              let tourMarkerAnchor = null;
              const showTourMarker = activeTourFlow === TOUR_FLOWS.browse && Array.isArray(locations) && locations.length > 0 && mapRef.current;
              if (showTourMarker) {
                const map = mapRef.current;
                const bounds = map.getBounds();
                const center = map.getCenter();
                const visible = locations.filter(loc =>
                  loc.loc_lat != null && loc.loc_lng != null &&
                  bounds.contains([loc.loc_lat, loc.loc_lng])
                );
                const activeInTimeline = visible.filter(loc => loc.years && loc.years.includes(year));
                const baseCandidates = activeInTimeline.length > 0 ? activeInTimeline : visible;
                // Prefer sample markers that have photos/video so the
                // "browse" onboarding tour points at something visually
                // rich. Fall back to all candidates if none have media.
                const withMedia = baseCandidates.filter(loc => loc.has_media);
                const candidates = withMedia.length > 0 ? withMedia : baseCandidates;
                if (candidates.length > 0) {
                  const closest = candidates.reduce((best, loc) => {
                    const d = Math.pow(loc.loc_lat - center.lat, 2) + Math.pow(loc.loc_lng - center.lng, 2);
                    return d < best.dist ? { loc, dist: d } : best;
                  }, { loc: candidates[0], dist: Infinity }).loc;
                  const containerRect = map.getContainer().getBoundingClientRect();
                  const point = map.latLngToContainerPoint([closest.loc_lat, closest.loc_lng]);
                  const anchorSize = 13;
                  const offset = anchorSize / 2;
                  tourMarkerAnchor = ReactDOM.createPortal(
                    <div
                      id="tour-marker-anchor"
                      style={{
                        position: 'fixed',
                        left: containerRect.left + point.x - offset,
                        top: containerRect.top + point.y - offset,
                        width: anchorSize,
                        height: anchorSize,
                        zIndex: 1100,
                        pointerEvents: 'none',
                      }}
                    />,
                    document.body
                  );
                }
              }
              return (
                <>
                  {tourMarkerAnchor}
                  <OnboardingTour
                    activeFlow={activeTourFlow}
                    hasVisibleMarker={tourMarkerAnchor !== null}
                    addPlaceButtonRef={addPlaceButtonRef}
                    timelineSliderRef={timelineSliderRef}
                    mapRef={mapRef}
                  />
                </>
              );
            })()}

            {/* Map Key Legend - Only visible in exhibition mode */}
            {isExhibitionMode && (
              <Paper
                elevation={3}
                sx={{
                  position: "absolute",
                  bottom: 24,
                  left: 20,
                  zIndex: 1000,
                  p: 1.5,
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: 2,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>
                  Key
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                  {/* Map Pin - Exhibition-Related Content */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 24, display: "flex", justifyContent: "center" }}>
                      <LocationOnIcon sx={{ color: "#000", fontSize: 22 }} />
                    </Box>
                    <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                      Exhibition-Related Content
                    </Typography>
                  </Box>
                  {/* Grey Square - Sites Added by Visitors */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 24, display: "flex", justifyContent: "center" }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: "#808080",
                          border: "1px solid #fff",
                          borderRadius: "2px",
                          boxShadow: "0 0 2px rgba(0,0,0,0.6)",
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                      Sites Added by Visitors
                    </Typography>
                  </Box>
                  {/* Yellow Square - User-Added Sites with Content about Selected Era */}
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <Box sx={{ width: 24, display: "flex", justifyContent: "center", flexShrink: 0, pt: 0.25 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: "#FFD700",
                          border: "1px solid #fff",
                          borderRadius: "2px",
                          boxShadow: "0 0 2px rgba(0,0,0,0.6)",
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                      Content Related to Selected Era on Timeline
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            )}

            {/* Help and About buttons - only visible in exhibition mode */}
            {isExhibitionMode && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 24,
                  right: 20,
                  zIndex: 1000,
                  display: "flex",
                  flexDirection: "row",
                  gap: 1,
                }}
              >
                <Button
                  sx={{
                    backgroundColor: "#fff !important",
                    color: "var(--theme-color) !important",
                    fontWeight: "900 !important",
                    textTransform: "unset !important",
                    lineHeight: "unset",
                    minWidth: "auto",
                    px: 2,
                  }}
                  onClick={() => setShowHelpDialog(true)}
                  variant="contained"
                  startIcon={<HelpOutlineIcon />}
                >
                  Help
                </Button>
                <Button
                  sx={{
                    backgroundColor: "#fff !important",
                    color: "var(--theme-color) !important",
                    fontWeight: "900 !important",
                    textTransform: "unset !important",
                    lineHeight: "unset",
                    minWidth: "auto",
                    px: 2,
                  }}
                  onClick={() => setShowAboutDialog(true)}
                  variant="contained"
                  startIcon={<InfoIcon />}
                >
                  About
                </Button>
              </Box>
            )}

            {/* Help Dialog - Large popup that shows map edges */}
            <Modal
              open={showHelpDialog}
              onClose={() => setShowHelpDialog(false)}
              aria-labelledby="help-dialog-title"
            >
              <Paper
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: { xs: "92%", sm: "85%", md: "75%", lg: "65%" },
                  maxWidth: 800,
                  maxHeight: "85vh",
                  overflow: "auto",
                  p: { xs: 3, sm: 4 },
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                  <Typography id="help-dialog-title" variant="h4" sx={{ fontWeight: "bold" }}>
                    Help
                  </Typography>
                  <IconButton onClick={() => setShowHelpDialog(false)} size="large">
                    <CloseIcon />
                  </IconButton>
                </Box>

                <Typography variant="h6" sx={{ fontWeight: "bold", mt: 2, mb: 1 }}>
                  To browse sites:
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: "1rem", lineHeight: 1.7 }}>
                  Click any dot on the map to display a panel with info, images, and comments about the place. Note the timeline slider at top: Dots highlighted in yellow are sites with content from the era selected on the timeline.
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "center", my: 2 }}>
                  <Box
                    component="img"
                    src="/exhibit_1.png"
                    alt="Browse sites example 1"
                    sx={{ maxWidth: "100%", width: { xs: "100%", sm: "45%" }, height: "auto", borderRadius: 1, boxShadow: 2 }}
                  />
                  <Box
                    component="img"
                    src="/exhibit_2.png"
                    alt="Browse sites example 2"
                    sx={{ maxWidth: "100%", width: { xs: "100%", sm: "45%" }, height: "auto", borderRadius: 1, boxShadow: 2 }}
                  />
                </Box>
                <Typography variant="body1" paragraph sx={{ fontSize: "1rem", lineHeight: 1.7 }}>
                  <strong>Note the time travel option:</strong> If content for the site includes multiple eras, you can scroll between eras using the forward and backward arrows next to the decade at the top of the panel for that location ("1950s," etc.).
                </Typography>

                <Typography variant="h6" sx={{ fontWeight: "bold", mt: 3, mb: 1 }}>
                  Interactivity: Commenting
                </Typography>
                <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, alignItems: "flex-start", my: 2 }}>
                  <Box
                    component="img"
                    src="/exhibit_3.png"
                    alt="Commenting example"
                    sx={{ width: { xs: "100%", sm: "40%" }, height: "auto", borderRadius: 1, boxShadow: 2, flexShrink: 0 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" paragraph sx={{ fontSize: "1rem", lineHeight: 1.7 }}>
                      To leave a comment on an existing location post, scroll down in the display panel to find and click "Leave a Comment." A text entry box will appear.
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ fontSize: "1rem", lineHeight: 1.7, fontStyle: "italic", fontWeight: "bold" }}>
                      NOTE: All comments will be reviewed for approval within 48 hours.
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="h6" sx={{ fontWeight: "bold", mt: 3, mb: 1 }}>
                  Interactivity: Adding Your Own Post
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: "1rem", lineHeight: 1.7 }}>
                  In addition to commenting on other location posts, you can add your own place to the map. Click "Add a New Place" (top right) to open a text entry panel where you will be asked to name and locate the place.
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: "1rem", lineHeight: 1.7 }}>
                  You may select the place on the map with your cursor or type in the address directly. Give the place a name and choose the decade you want to write about. In the "Story of Place" section, you can add a memory, a fact, a narrative or a question for us (or any other visitor).
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: "1rem", lineHeight: 1.7 }}>
                  Once completed, simply click "Submit" and your post will be sent to reviewers for approval. You may include your name or remain anonymous. Content loaded to the map will be made visible on the platform during the exhibit's run and then archived with other exhibition assets.
                </Typography>

                <Typography variant="h6" sx={{ fontWeight: "bold", mt: 3, mb: 1 }}>
                  If you want to learn more
                </Typography>
                <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, alignItems: "flex-start", my: 2 }}>
                  <Box
                    component="img"
                    src="/exhibit_4.png"
                    alt="Learn more example"
                    sx={{ width: { xs: "100%", sm: "40%" }, height: "auto", borderRadius: 1, boxShadow: 2, flexShrink: 0 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" paragraph sx={{ fontSize: "1rem", lineHeight: 1.7 }}>
                      about the Map250 platform for use at home, in a classroom, in an exhibit – or if you have other place-based assets (photos, recordings, etc.) that you'd like to include on the map, please reach out to Whose Land via their website:{" "}
                      <a
                        href="https://www.whoseland.org"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "var(--theme-color)", textDecoration: "underline", fontWeight: "bold" }}
                      >
                        whoseland.org
                      </a>
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Modal>

            {/* About Dialog - Large popup that shows map edges */}
            <Modal
              open={showAboutDialog}
              onClose={() => setShowAboutDialog(false)}
              aria-labelledby="about-dialog-title"
            >
              <Paper
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: { xs: "92%", sm: "85%", md: "75%", lg: "65%" },
                  maxWidth: 800,
                  maxHeight: "85vh",
                  overflowY: "auto",
                  overflowX: "hidden",
                  p: { xs: 3, sm: 4 },
                  borderRadius: 2,
                  boxSizing: "border-box",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                  <Typography id="about-dialog-title" variant="h4" sx={{ fontWeight: "bold" }}>
                    About
                  </Typography>
                  <IconButton onClick={() => setShowAboutDialog(false)} size="large">
                    <CloseIcon />
                  </IconButton>
                </Box>
                <Box
                  sx={{ fontSize: "1.1rem", lineHeight: 1.7, wordWrap: "break-word", overflowWrap: "break-word", "& p": { mb: 2 }, "& strong": { fontWeight: "bold" }, "& em": { fontStyle: "italic" } }}
                  dangerouslySetInnerHTML={{ __html: project?.exhibition_about_html || `<p>Map250 is a resource developed by Whose Land, an educational nonprofit that responds to the histories and legacies of land displacement and dispossession in America through collaborative community-based humanities projects.</p><p>The Map250 platform is designed with collaboration and community input in mind. Currently being used in over a dozen classrooms, colleges, tribal nations and community group settings, the map invites users to share place-based stories, memories, photographs and other historical memorabilia by location and historical era.</p><p>The resulting historical layering of stories over single sites allows users to discover changes in the land and in the built environment and to discover the impact on the resident people and ecology in each place.</p><p><strong>Map250 is interactive!</strong></p><p>You can add and share place-based stories, comment on locations already featured on the map, or respond to the entries and input of other visitors. You can also ask questions about locations and other posts. We'd love your input!</p><p><em>Visitor posts will be reviewed and published within 48 hours.</em></p>` }}
                />
              </Paper>
            </Modal>

            {mapCenter && zoom ? (
              // Map-area wrapper. The FilterBar used to live here, but it
              // was hoisted ABOVE the map + side-panel Grid (see the Box
              // wrapper above) so the top bar always spans the full screen
              // width — opening the side panel no longer shrinks it. This
              // wrapper now just holds the MapContainer + its map-level
              // overlay buttons (Type of Entry, Regions, Add a New Place)
              // (Add a New Place, etc.).
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  width: "100%",
                }}
              >
                <div
                  style={{
                    position: "relative",
                    flex: "1 1 auto",
                    minHeight: 0,
                    width: "100%",
                  }}
                >
                  {/* Type of Entry (June 2026 slim-down): compact
                      All / Histories / Memories toggle floating at the
                      visible map's top-left, pulled out of the Display
                      Settings drawer so it's always visible yet separate
                      from the filter strip's category chips. */}
                  {!isExhibitionMode && (
                    <Box
                      sx={{
                        position: "absolute",
                        // At phone widths the three labeled entry-type
                        // segments and Add a New Place cannot share one row.
                        // Stack the toggle below the action instead of letting
                        // two fixed overlays collide.
                        top: { xs: "64px", sm: "10px" },
                        left: "10px",
                        zIndex: 1000,
                      }}
                    >
                      <EntryTypeToggle />
                    </Box>
                  )}
                  {/* Show / Hide Regions (June 2026 slim-down): live
                      toggle at the visible map's bottom-left. Filled when
                      regions are showing. */}
                  {!isExhibitionMode && (
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: "24px",
                        left: "20px",
                        zIndex: 1000,
                      }}
                    >
                      <Button
                        variant="contained"
                        onClick={() => setRegionsVisible(!showRegions)}
                        aria-pressed={showRegions}
                        sx={{
                          backgroundColor: showRegions
                            ? "var(--theme-color) !important"
                            : "#fff !important",
                          color: showRegions
                            ? "#fff !important"
                            : "var(--theme-color) !important",
                          fontWeight: "900 !important",
                          textTransform: "unset !important",
                          lineHeight: "unset",
                        }}
                      >
                        {showRegions ? "Hide Regions" : "Show Regions"}
                      </Button>
                    </Box>
                  )}
                  {/* "Add a New Place" — pinned to the top-RIGHT of the
                      visible map area, directly above the zoom in/out
                      stack. Leaflet's zoom column is moved to the right
                      edge by MapComponent.css (`.leaflet-left` →
                      `right: 10px`), and that same file adds a
                      `margin-top` to `.leaflet-control-zoom` so the
                      zoom buttons clear this button by ~10px. Anchoring
                      both at `right: 10px` keeps them vertically
                      aligned even when the map is resized. */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: "10px",
                      right: "10px",
                      zIndex: 1000,
                    }}
                  >
                    <Button
                      ref={addPlaceButtonRef}
                      variant="contained"
                      onClick={() => {
                        if (activeTourFlow) {
                          setActiveTourFlow(null);
                        }
                        addLocation();
                      }}
                      sx={{
                        backgroundColor: "#fff !important",
                        color: "var(--theme-color) !important",
                        fontWeight: "900 !important",
                        textTransform: "unset !important",
                        lineHeight: "unset",
                      }}
                    >
                      Add a New Place
                    </Button>
                  </Box>
                <MapContainer
                  center={mapCenter}
                  zoom={zoom}
                  ref={mapRef}
                  style={{ height: "100%", width: "100%", zIndex: 80 }}
                >
                  <MapCenterUpdater center={mapCenter} zoomLevel={zoom} projectId={project.projectsid} />
                  <TileLayer
                    url={selectedBaseLayer?.url}
                    attribution={selectedBaseLayer?.attribution}
                    maxZoom={selectedBaseLayer?.max_zoom}
                    maxNativeZoom={selectedBaseLayer?.max_native_zoom}
                  />

                  {selectedLayer && (
                    <TileLayer
                      key={selectedLayer.id}
                      url={selectedLayer.url}
                      maxZoom={selectedLayer.max_zoom || 20}
                      maxNativeZoom={selectedLayer.max_native_zoom || 20}
                      opacity={overlayOpacity}
                    />
                  )}
                  {/*<FeatureGroup>
                    <EditControl
                        position="topright"
                        onCreated={handlePolygonCreated}
                        draw={{
                            rectangle: false,
                            circle: false,
                            circlemarker: false,
                            marker: false,
                            polyline: false,
                            polygon: isDrawingPolygon, // Enable polygon drawing based on state
                        }}
                    />
                </FeatureGroup>*/}

                  {Array.isArray(locations) &&
                    locations.length > 0 &&
                    locations
                      .filter((loc) => {
                        // Filter based on user and permissions
                        if (!user && !isExhibitionMode && (!loc.has_public || !loc.has_approved)) {
                          return false;
                        }
                        if (
                          (loc.has_public || loc.has_project) &&
                          !loc.has_approved &&
                          !loc.has_my_stories
                        ) {
                          return false;
                        }
                        if (showOnlyVetted && !loc.is_reliable) return false;
                        if (
                          showOnlyMapGroup &&
                          (!loc.has_project || !loc.has_approved)
                        ) {
                          return false;
                        }
                        if (!passesShowMyPlaces(!!loc.has_my_stories)) return false;
                        return true;
                      })
                      .filter(
                        (loc) =>
                          typeof loc.loc_lat !== "undefined" &&
                          typeof loc.loc_lng !== "undefined" &&
                          loc.loc_lat != null &&
                          loc.loc_lng != null,
                      ) // Valid coordinates only
                      // Thematic filter (Phase 3 #9). When no filter is
                      // active, shapePassesFilter is a no-op true → no
                      // wasted work in the common case. Date-Added uses
                      // the rolled-up max created_at across the
                      // location's constituent contributions.
                      .filter((loc) => {
                        // OUT-OF-ERA markers stay visible under a theme
                        // filter, showing the sites that carry that theme in
                        // at least one era (client request Aug 4 2026:
                        // "keep some way of ghosting sites for non-eras even
                        // when theme filters are employed").
                        //
                        // loc.taxonomy_ids is rolled up across ALL of the
                        // location's decades, so this plain filter already
                        // expresses "related to the theme in some era". An
                        // era-blind early return used to sit here suppressing
                        // them entirely; it read the Themes spec ("we don't
                        // know the theme if not yet posted. We can't assume")
                        // as a reason to hide rather than to soften.
                        //
                        // The spec still holds, because the marker renders as
                        // a hollow OUTLINE in the matched era's hue — saying
                        // the place relates to the theme in another decade,
                        // not that it did in this one. A solid square would
                        // make that second claim; an outline doesn't.
                        // See outOfEraMarkerColor for the hue.
                        return shapePassesFilter(
                          loc.taxonomy_ids,
                          loc.created_at_max,
                        );
                      })
                      .map((loc) => {
                        const currentDecade = Math.floor(Number(year) / 10) * 10;
                        const isInTimeline = Array.isArray(loc.years) && loc.years.some(
                          (value) => Math.floor(Number(value) / 10) * 10 === currentDecade,
                        );
                        const entryKind = config.ENTRY_TYPE_SHAPES_ENABLED
                          ? getLocationEntryKind(loc)
                          : ENTRY_KIND.HISTORY;
                        const hoverLabel = getMarkerHoverLabel({
                          loc,
                          year,
                          isInTimeline,
                        });
                        const markerAccessibleLabel = `${getEntryKindLabel(entryKind)}: ${hoverLabel?.text || loc.address || "map location"}`;
                        // Era cue (client revision June-18, item 7):
                        //   • Current decade → SOLID square in the assigned
                        //     taxonomy hue (always-on color via colorForShape,
                        //     Primary > Secondary > Tertiary through
                        //     theme_ranks), or solid black when the shape has
                        //     no active-kind chip / only a catch-all.
                        //   • Other era → hollow outline in the hue of the
                        //     nearest era carrying an active-kind chip
                        //     (client request Aug 4 2026, extended to the
                        //     unfiltered view Aug 5), so the outline, the
                        //     hover label and the era its popup jumps to all
                        //     describe one decade. Neutral grey only when no
                        //     era has such a chip. See outOfEraMarkerColor.
                        const markerColor = isInTimeline
                          ? colorForShape(
                              loc.taxonomy_ids,
                              MARKER_NEUTRAL_CURRENT,
                              loc.theme_ranks,
                            )
                          : outOfEraMarkerColor(loc);

                        const iconSizes = {
                          medium: [24, 30],
                          mediumAnchor: [12, 30],
                        };

                        let icon;

                        // "Show My Places" (client feedback #8): render the
                        // viewer's OWN sites as LARGE BLACK pins layered on
                        // top of everyone else's markers (see isShowingMyPlaces
                        // above) — restoring the pre-redesign behavior where
                        // "Show My Places" dropped big black markers for the
                        // user's contributions. Applies in every era (a
                        // contributor wants to see all of their pins), so it
                        // takes precedence over the square era cue.
                        const isMyPlaceHighlight =
                          isShowingMyPlaces && loc.has_my_stories;
                        if (isMyPlaceHighlight) {
                          icon = L.divIcon({
                            html: renderToString(
                              <LocationOnIcon
                                style={{ color: "#000000", fontSize: 44 }}
                              />,
                            ),
                            className: "custom-icon",
                            iconSize: [44, 44],
                            iconAnchor: [22, 44],
                          });
                        }

                        //  Exhibition Mode with Reliable/Exhibition Content → Show Map Pin (yellow if in era, black if not)
                        else if (isExhibitionMode && loc.is_reliable) {
                          const pinColor = isInTimeline ? "#FFBF36" : "#000000";
                          icon = L.divIcon({
                            html: renderToString(
                              <LocationOnIcon
                                style={{ color: pinColor, fontSize: 30 }}
                              />,
                            ),
                            className: "custom-icon",
                            iconSize: iconSizes.medium,
                            iconAnchor: iconSizes.mediumAnchor,
                          });
                        }

                        // Default entry marker. DOC-13 adds an orthogonal
                        // story-type cue without disturbing the existing era
                        // and taxonomy cues: histories are squares, memories
                        // are circles, and a location containing both renders
                        // the two symbols side by side at one map anchor.
                        else {
                          icon = L.divIcon(
                            entryTypeMarkerIcon({
                              color: markerColor,
                              isInTimeline,
                              entryKind,
                            }),
                          );
                        }

                        return (
                          <Marker
                            key={`${loc.ids.join("-")}-${showMyPlaces}-${highlightMyPlaces}-${currentDecade}-${entryKind}`}
                            position={[loc.loc_lat, loc.loc_lng]}
                            // Raise the viewer's own "my places" black pins
                            // above every other marker so they read as an
                            // overlay highlight (client feedback #8).
                            zIndexOffset={isMyPlaceHighlight ? 1000 : 0}
                            // The native `title` HTML attribute used to be
                            // the only hover affordance — we now have a
                            // real Leaflet Tooltip (below) that handles
                            // both the in-era and grey-era labels with
                            // proper styling. Suppress `title` so the
                            // browser's default yellow box doesn't fight
                            // the Leaflet tooltip on hover.
                            title={undefined}
                            // Wizard "Click a spot on the map" mode AND
                            // polygon drawing mode: disable interactivity so
                            // clicks pass through to the map click handler.
                            // For point placement, that handler snaps to
                            // this marker's lat/lng (≤50ft threshold) and
                            // geocodes. For polygon drawing, the click is
                            // consumed as the next vertex. Either way,
                            // opening the marker's side panel here would
                            // hijack the user's intent. Leaflet's own click
                            // stopPropagation would prevent the underlying
                            // map handler from firing if we only gated the
                            // eventHandler — dropping `interactive` makes
                            // the icon paint pointer-events:none so the
                            // click reaches the map. Early-return below is
                            // belt-and-suspenders in case a future refactor
                            // flips the default.
                            interactive={!isDrawingOrPlacingShape}
                            icon={icon}
                            eventHandlers={{
                              add: (event) => {
                                // Leaflet applies `alt` only to IMG icons. Our
                                // L.divIcon markers are focusable DIV buttons,
                                // so name the actual marker element explicitly.
                                const element = event.target.getElement?.();
                                if (element) {
                                  element.setAttribute("aria-label", markerAccessibleLabel);
                                }
                              },
                              click: () => {
                                if (isDrawingOrPlacingShape) return;
                                handleMarkerClick(loc.ids);
                              },
                            }}
                          >
                            {hoverLabel && (
                              <LeafletTooltip
                                direction="top"
                                offset={[0, -10]}
                                opacity={0.95}
                                className={
                                  hoverLabel.muted
                                    ? "marker-hover-label marker-hover-label--muted"
                                    : "marker-hover-label"
                                }
                              >
                                {hoverLabel.text}
                              </LeafletTooltip>
                            )}
                          </Marker>
                        );
                      })}

                  {/* Temporary marker for wizard location selection */}
                  {showWizard && selectedAddressLatLng && (
                    <Marker
                      key="wizard-temp-marker"
                      position={[selectedAddressLatLng.lat, selectedAddressLatLng.lng]}
                      icon={L.divIcon({
                        html: renderToString(
                          <LocationOnIcon
                            style={{ color: "#C8A96E", fontSize: 36 }}
                          />,
                        ),
                        className: "custom-icon",
                        iconSize: [36, 36],
                        iconAnchor: [18, 36],
                      })}
                      interactive={false}
                    />
                  )}

                  {/* Polygon Regions */}
                  {showRegions &&
                    Array.isArray(polygons) &&
                    polygons.length > 0 &&
                    polygons.map((polygon) => {
                        if (!passesShowMyPlaces(!!polygon.has_my_stories)) {
                          return null;
                        }
                        const coordinates = normalizePolygonCoordinates(
                          polygon.coordinates,
                        );
                        if (coordinates.length < 3) return null;
                        // Thematic filter: hide polygons that don't match
                        // active chips, or fall outside the active Date
                        // Added window. No-op true when no filter is on.
                        if (
                          !shapePassesFilter(
                            polygon.taxonomy_ids,
                            polygon.created_at,
                          )
                        )
                          return null;
                        const isInTimeline = Number(polygon.year) === Number(year);
                        const isHovered = hoveredPolygonId === polygon.id;
                        const isSelected = selectedPolygonId === polygon.id;
                        const highlight = isHovered || isSelected;
                        // Thematic v2 polygon rules (plan §G):
                        //   G1 Out-of-era → grey outline only, no fill.
                        //   G2 In-era + no theme → grey fill.
                        //   G3 In-era + theme  → fill = theme color (E3).
                        // The old in-era green (#2e6b4f) is gone — in-era
                        // "this region just doesn't have a chip" reads as
                        // neutral grey now, leaving the saturated palette
                        // exclusively for themed polygons. Out-of-era reads
                        // as a faint geographic hint (outline only).
                        const POLYGON_NEUTRAL_GREY = "#8a8a8a";
                        // Always-on color for the CURRENT era (item 7 keeps the
                        // assigned hue on in-era regions; other-era is forced
                        // grey below via eraColor). Falls back to grey when the
                        // polygon has no active-kind chip / only a catch-all.
                        // theme_ranks picks Primary > Secondary > Tertiary.
                        // Selection/hover still wins via `highlight` below.
                        const baseColor = colorForShape(
                          polygon.taxonomy_ids,
                          POLYGON_NEUTRAL_GREY,
                          polygon.theme_ranks,
                        );
                        // Item 7 (mirror of marker rule): current-era regions
                        // paint the assigned chip hue (fill + stroke); other-era
                        // regions are ALWAYS neutral grey (outline only) — we
                        // don't assume a theme for a decade with no posted
                        // story. Highlight (hover/select) still wins below.
                        const eraColor = isInTimeline
                          ? baseColor
                          : POLYGON_NEUTRAL_GREY;
                        return (
                          <Polygon
                            key={polygon.id}
                            positions={coordinates}
                            // ISSUE-003: Leaflet polygon hit-tests through the
                            // SVG fill, not the stroke. The previous floor of
                            // 0.06 (out-of-timeline) / 0.12 (in-timeline) made
                            // outlined regions visually invisible AND nearly
                            // impossible to click reliably — users had to
                            // hunt for the cursor change. Bump the floor so
                            // every polygon has a real, painted hit target
                            // while still letting the map breathe through.
                            // interactive defaults to true on react-leaflet
                            // Polygon, but set it explicitly to lock the
                            // contract in case a future refactor wraps these
                            // in a layer that flips the default.
                            //
                            // Wizard "Click a spot on the map" mode AND
                            // polygon drawing mode: disable interactivity so
                            // clicks PASS THROUGH to the map container and
                            // the useMapEvents handler fires. Leaflet Path's
                            // click handler calls fakeStop() which halts DOM
                            // propagation, so early-returning from the
                            // handler isn't enough — the polygon still
                            // swallows the event. Dropping interactive
                            // entirely makes the SVG paint
                            // pointer-events:none and the click reaches the
                            // map handler. For point placement that means
                            // geocode + snap; for polygon drawing it means
                            // the next vertex is captured even when the
                            // cursor is over an existing region.
                            interactive={!isDrawingOrPlacingShape}
                            pathOptions={{
                              // Item 7: current-era strokes/fills the chip hue;
                              // other-era is forced neutral grey (eraColor),
                              // outline-only. Highlight always wins.
                              color: highlight ? "#1a73e8" : eraColor,
                              weight: highlight ? 3 : 2,
                              dashArray: config.ENTRY_TYPE_SHAPES_ENABLED
                                ? entryKindDashArray(polygon.entry_kind)
                                : undefined,
                              // Other-era grey outline at 0.7 opacity — a touch
                              // bolder than the old 0.5 so a muted grey doesn't
                              // vanish on the cream map now that it carries no
                              // hue.
                              opacity: highlight ? 1 : isInTimeline ? 1 : 0.7,
                              fillColor: highlight ? "#1a73e8" : eraColor,
                              // ISSUE-003 tradeoff: out-of-era polygons drop
                              // to fillOpacity:0 (outline-only) per plan §G1.
                              // This makes them visually quieter AND means
                              // Leaflet's fill-based hit-test goes away for
                              // out-of-era — by design: out-of-era regions
                              // should not steal clicks from in-era pins.
                              fillOpacity: highlight
                                ? 0.28
                                : isInTimeline
                                  ? 0.20
                                  : 0,
                            }}
                            eventHandlers={{
                              click: (e) => {
                                // Wizard placing/drawing modes: the user is
                                // either putting a point inside this polygon
                                // (marker mode) or extending their own new
                                // polygon (polygon mode). In both cases the
                                // existing polygon's "open me" intent is
                                // wrong. Falling through lets the map click
                                // handler do the right thing. Without this
                                // guard polygonClickedRef would be set and
                                // the map-level handler early-returns,
                                // swallowing the wizard input entirely.
                                if (isDrawingOrPlacingShape) return;
                                polygonClickedRef.current = true;
                                handlePolygonClick(polygon, e.latlng);
                              },
                            }}
                          />
                        );
                      })}
                  {polygonCoordinates.length >= 3 &&
                    selectionType === SHAPE_TYPE.POLYGON && (
                      <Polygon
                        positions={polygonCoordinates}
                        interactive={false}
                        pathOptions={{
                          color: "#1a73e8",
                          weight: 2,
                          fillColor: "#1a73e8",
                          fillOpacity: 0.15,
                          className: "polygon-selection-overlay",
                        }}
                      />
                    )}
                  {isDrawingPolygon && drawingPoints.length > 0 && (
                    <>
                      <Polyline
                        positions={drawingPoints}
                        pathOptions={{
                          color: "#1a73e8",
                          weight: 2,
                          dashArray: "6 6",
                        }}
                      />
                      {drawingPoints.map((point, index) => (
                        <CircleMarker
                          key={`${point[0]}-${point[1]}-${index}`}
                          center={point}
                          radius={4}
                          pathOptions={{
                            color: "#1a73e8",
                            weight: 2,
                            fillColor: "#fff",
                            fillOpacity: 1,
                          }}
                          eventHandlers={
                            index === 0 && drawingPoints.length >= 3
                              ? {
                                  click: () => {
                                    handlePolygonDrawClick({ lat: point[0], lng: point[1] });
                                  },
                                  mouseover: (e) => {
                                    e.target.getElement().style.cursor = "pointer";
                                  },
                                }
                              : {}
                          }
                        />
                      ))}
                    </>
                  )}
                  {/* Existing Polylines (line tool) */}
                  {showRegions &&
                    Array.isArray(polylines) &&
                    polylines.length > 0 &&
                    polylines.map((polyline) => {
                      if (!passesShowMyPlaces(!!polyline.has_my_stories)) {
                        return null;
                      }
                      const coordinates = normalizePolygonCoordinates(
                        polyline.coordinates,
                      );
                      if (coordinates.length < 2) return null;
                      // Thematic filter: hide polylines that don't match
                      // active chips, or fall outside the active Date
                      // Added window. No-op when no filter is on.
                      if (
                        !shapePassesFilter(
                          polyline.taxonomy_ids,
                          polyline.created_at,
                        )
                      )
                        return null;
                      const isInTimeline = Number(polyline.year) === Number(year);
                      const isHovered = hoveredPolylineId === polyline.id;
                      const isSelected = selectedPolylineId === polyline.id;
                      const highlight = isHovered || isSelected;
                      // Polyline parallel to polygon rules (item 7): current
                      // era strokes the chip hue, other era is forced neutral
                      // grey (eraColor below), with opacity carrying the extra
                      // recede. Lines have no fill.
                      const POLYLINE_NEUTRAL_GREY = "#8a8a8a";
                      // Always-on color for the CURRENT era (other-era forced
                      // grey below). Falls back to grey when the line has no
                      // active-kind chip / only a catch-all. theme_ranks prefers
                      // Primary over Secondary/Tertiary among matching themes.
                      const baseColor = colorForShape(
                        polyline.taxonomy_ids,
                        POLYLINE_NEUTRAL_GREY,
                        polyline.theme_ranks,
                      );
                      // Item 7 (mirror of marker rule): current-era lines paint
                      // the chip hue; other-era lines are ALWAYS neutral grey.
                      const eraColor = isInTimeline
                        ? baseColor
                        : POLYLINE_NEUTRAL_GREY;
                      return (
                        <Polyline
                          key={`polyline-${polyline.id}`}
                          positions={coordinates}
                          interactive={!isDrawingOrPlacingShape}
                          pathOptions={{
                            // Item 7: current-era strokes the chip hue; other-era
                            // is forced neutral grey (eraColor) and recedes via
                            // the opacity drop below. Highlight wins.
                            color: highlight ? "#1a73e8" : eraColor,
                            weight: highlight ? 5 : 4,
                            dashArray: config.ENTRY_TYPE_SHAPES_ENABLED
                              ? entryKindDashArray(polyline.entry_kind)
                              : undefined,
                            opacity: highlight ? 0.95 : isInTimeline ? 0.85 : 0.6,
                          }}
                          eventHandlers={{
                            click: () => {
                              if (isDrawingOrPlacingShape) return;
                              polylineClickedRef.current = true;
                              handlePolylineClick(polyline);
                            },
                            mouseover: () => {
                              if (isDrawingOrPlacingShape) return;
                              setHoveredPolylineId(polyline.id);
                            },
                            mouseout: () => {
                              setHoveredPolylineId((prev) =>
                                prev === polyline.id ? null : prev,
                              );
                            },
                          }}
                        />
                      );
                    })}
                  {/* Selected line highlight overlay (mirrors polygon's
                      selection overlay; non-interactive so it doesn't block
                      clicks on the underlying polyline). */}
                  {lineCoordinates.length >= 2 &&
                    selectionType === SHAPE_TYPE.LINE && (
                      <Polyline
                        positions={lineCoordinates}
                        interactive={false}
                        pathOptions={{
                          color: "#1a73e8",
                          weight: 3,
                          opacity: 0.85,
                          className: "polyline-selection-overlay",
                        }}
                      />
                    )}
                  {/* In-progress line drawing */}
                  {isDrawingLine && drawingPoints.length > 0 && (
                    <>
                      <Polyline
                        positions={drawingPoints}
                        pathOptions={{
                          color: "#1a73e8",
                          weight: 2,
                          dashArray: "6 6",
                        }}
                      />
                      {drawingPoints.map((point, index) => (
                        <CircleMarker
                          key={`line-${point[0]}-${point[1]}-${index}`}
                          center={point}
                          radius={4}
                          pathOptions={{
                            color: "#1a73e8",
                            weight: 2,
                            fillColor: "#fff",
                            fillOpacity: 1,
                          }}
                          eventHandlers={
                            // Click the most recent vertex to finish the
                            // line — same affordance as polygon's "click
                            // first vertex to close" but for the open
                            // path's last point.
                            index === drawingPoints.length - 1 && drawingPoints.length >= 2
                              ? {
                                  click: () => {
                                    finishLineDrawing(null);
                                  },
                                  mouseover: (e) => {
                                    e.target.getElement().style.cursor = "pointer";
                                  },
                                }
                              : {}
                          }
                        />
                      ))}
                    </>
                  )}
                  {/* Markers */}
                  {!showWizard && selectedAddressLatLng && (
                    <Marker
                      position={selectedAddressLatLng}
                      icon={L.divIcon({
                        html: renderToString(
                          <LocationOnIcon
                            style={{ color: "black", fontSize: 40 }}
                          />,
                        ),
                        className: "custom-icon",
                        iconSize: [30, 40],
                        iconAnchor: [15, 40],
                      })}
                    />
                  )}
                  {suggestedLatLng && showSuggestionPopup && (
                    <Marker
                      position={suggestedLatLng}
                      icon={L.divIcon({
                        html: renderToString(
                          <LocationOnIcon
                            style={{ color: "#1E6BE3", fontSize: 40 }}
                          />,
                        ),
                        className: "suggested-marker",
                        iconSize: [30, 40],
                        iconAnchor: [15, 40],
                      })}
                    />
                  )}
                  <MapEventsHandler />
                </MapContainer>
                </div>
              </div>
            ) : (
              <div>Loading map...</div>
            )}
            {/* Empty Era Popup Overlay */}
            {emptyEraPopup && (() => {
              const decade = Math.floor(year / 10) * 10;
              const isNoStories = emptyEraPopup.noStories;
              const hasEarlier = !isNoStories && emptyEraPopup.availableYears.some((y) => y < decade);
              const hasLater = !isNoStories && emptyEraPopup.availableYears.some((y) => y > decade);

              const restorePolygonState = () => {
                const info = emptyEraPopup.polygonInfo;
                if (info) {
                  setPolygonData({ id: info.id, coordinates: info.coordinates });
                  setSelectedPolygonId(info.id);
                  setPolygonCoordinates(info.coordinates);
                  setSelectionType(SHAPE_TYPE.POLYGON);
                  setSelectedMarkerIds([]);
                } else {
                  setSelectedMarkerIds(emptyEraPopup.markerIds);
                }
              };

              const handleJumpBack = () => {
                const earlierYears = emptyEraPopup.availableYears.filter((y) => y < decade);
                if (earlierYears.length === 0) return;
                const target = earlierYears[earlierYears.length - 1];
                setEmptyEraPopup(null);
                setYear(target);
                restorePolygonState();
                if (!emptyEraPopup.polygonInfo) {
                  setSelectedAddressLatLng({ lat: emptyEraPopup.lat, lng: emptyEraPopup.lng });
                }
                setIsSidePanelOpen(true);
              };

              const handleJumpForward = () => {
                const laterYears = emptyEraPopup.availableYears.filter((y) => y > decade);
                if (laterYears.length === 0) return;
                const target = laterYears[0];
                setEmptyEraPopup(null);
                setYear(target);
                restorePolygonState();
                if (!emptyEraPopup.polygonInfo) {
                  setSelectedAddressLatLng({ lat: emptyEraPopup.lat, lng: emptyEraPopup.lng });
                }
                setIsSidePanelOpen(true);
              };

              const handlePopupAddPlace = () => {
                const popupLat = emptyEraPopup.lat;
                const popupLng = emptyEraPopup.lng;
                setEmptyEraPopup(null);
                setSelectedAddressLatLng({ lat: popupLat, lng: popupLng });
                if (!selectedAddress) {
                  setSelectedAddress(`${popupLat.toFixed(6)}, ${popupLng.toFixed(6)}`);
                }
                setAddStoryMode(true);
                setShowContribution(true);
                setIsSidePanelOpen(false);
                setStoryVisibility(getDefaultVisibility());
              };

              // Shared with the map-click and timeline-change dismissal paths
              // so all three clear the black search pin identically.
              const handleClose = dismissEmptyEraPopup;

              return (
                <Paper
                  elevation={6}
                  sx={{
                    position: "absolute",
                    top: "10%",
                    right: 48,
                    zIndex: 1100,
                    maxWidth: 420,
                    width: { xs: "90%", sm: "33.33%", md: "33.33%" },
                    p: 4,
                    borderRadius: 3,
                    backgroundColor: "rgba(255,255,255,0.97)",
                  }}
                >
                  <IconButton
                    aria-label="Close location message"
                    onClick={handleClose}
                    sx={{ position: "absolute", top: 8, right: 8 }}
                    size="small"
                  >
                    <CloseIcon />
                  </IconButton>

                  {isNoStories ? (
                    <>
                      <Typography
                        sx={{
                          fontSize: "1.25rem",
                          color: "var(--theme-color)",
                          fontWeight: 600,
                          textAlign: "center",
                          mb: 2,
                          pr: 3,
                        }}
                      >
                        No stories have been added for this location yet
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: "1.05rem",
                          color: "var(--theme-color)",
                          fontWeight: 400,
                          textAlign: "center",
                          mb: 2.5,
                          lineHeight: 1.5,
                        }}
                      >
                        Do you have a story or history about this location that you'd like to share?
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography
                        sx={{
                          fontSize: "1.15rem",
                          color: "var(--theme-color)",
                          fontWeight: 500,
                          textAlign: "center",
                          mb: 0.5,
                          pr: 3,
                        }}
                      >
                        No content yet added for the
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: "2.2rem",
                          fontWeight: 800,
                          color: "var(--theme-color)",
                          textAlign: "center",
                          mb: 0.5,
                        }}
                      >
                        {decade}s
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: "1.05rem",
                          color: "var(--theme-color)",
                          fontWeight: 400,
                          textAlign: "center",
                          mb: 2.5,
                          lineHeight: 1.5,
                        }}
                      >
                        {/* Client request: highlight the matched address
                            "in some way (color? Italics? Diff font?) without
                            adding space" so the user is reminded this popup
                            answers THEIR address search. Italic + weight
                            only — no margin/padding, so the line doesn't
                            grow. */}
                        {selectedAddress ? (
                          <>
                            for{" "}
                            <Box
                              component="span"
                              sx={{ fontStyle: "italic", fontWeight: 700 }}
                            >
                              {selectedAddress}
                            </Box>
                          </>
                        ) : (
                          "for this location"
                        )}
                      </Typography>
                    </>
                  )}

                  <Box sx={{ display: "flex", justifyContent: "center", mb: 2.5 }}>
                    <Button
                      variant="contained"
                      onClick={handlePopupAddPlace}
                      startIcon={<AddCircleIcon />}
                      sx={{
                        height: 48,
                        backgroundColor: "var(--theme-color) !important",
                        color: "var(--theme-btn-clr) !important",
                        fontWeight: "900 !important",
                        textTransform: "none !important",
                        fontSize: "1rem",
                        px: 4,
                      }}
                    >
                      Add a {decade}s Story
                    </Button>
                  </Box>

                  {(hasEarlier || hasLater) && (
                    <>
                      {/* Client request: replace the two-line
                          "Or / See user stories for this place for other
                          eras:" with a single heading. */}
                      <Typography
                        sx={{
                          fontSize: "0.95rem",
                          color: "var(--theme-color)",
                          fontWeight: 500,
                          textAlign: "center",
                          mb: 1.5,
                          mt: 1,
                        }}
                      >
                        User Stories for this Place:
                      </Typography>
                    </>
                  )}

                  {(hasEarlier || hasLater) && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1.5,
                      }}
                    >
                      {hasEarlier && (
                        <Button
                          variant="text"
                          onClick={handleJumpBack}
                          startIcon={<ArrowBackIcon sx={{ fontSize: 24 }} />}
                          sx={{
                            minHeight: 44,
                            color: "var(--theme-color)",
                            fontSize: "0.85rem",
                            fontWeight: 600,
                            textTransform: "none",
                            px: 1.5,
                          }}
                        >
                          Go back in time
                        </Button>
                      )}

                      {hasEarlier && hasLater && (
                        <Typography sx={{ color: "var(--theme-color)", opacity: 0.4, fontSize: "1.2rem" }}>|</Typography>
                      )}

                      {hasLater && (
                        <Button
                          variant="text"
                          onClick={handleJumpForward}
                          endIcon={<ArrowForwardIcon sx={{ fontSize: 24 }} />}
                          sx={{
                            minHeight: 44,
                            color: "var(--theme-color)",
                            fontSize: "0.85rem",
                            fontWeight: 600,
                            textTransform: "none",
                            px: 1.5,
                          }}
                        >
                          Go forward in time
                        </Button>
                      )}
                    </Box>
                  )}
                </Paper>
              );
            })()}

            {!showContribution && showOverlayOpacity && (
              <Box
                sx={{
                  position: "absolute",
                  // Aug 2026 client feedback ("separate the 'show regions'
                  // button from the opacity slider"): the "Show / Hide
                  // Regions" button is pinned bottom-left at left:20/bottom:24.
                  // On narrow viewports there is no room to sit beside it, so
                  // the slider stacks ABOVE the button instead of colliding
                  // with it.
                  bottom: { xs: 76, sm: 16 },
                  zIndex: 100,
                  // The slider lives inside the map Grid item (position:
                  // relative) so it lays out within whatever width the map
                  // column currently has.
                  ...(isSidePanelOpen
                    ? {
                        // Side panel open -> the map column is narrow and the
                        // bottom-right action buttons (View Table Mode, Add a
                        // New Place; zIndex 1000) sit inside it. A centered
                        // 80%-wide slider used to slide UNDER those buttons,
                        // so its right end wasn't clickable (client bug, July
                        // 2026: "opacity slider isn't functional ... hidden
                        // behind the Table View and other buttons"). Left-
                        // anchor it with right clearance so it clears the
                        // button column entirely.
                        //
                        // The left anchor was 16, which put the slider's left
                        // edge straight on top of the Show/Hide Regions
                        // button. Clear that button on sm+ ("keeping 'show
                        // regions' all the way left").
                        left: { xs: 16, sm: 190 },
                        right: { xs: 16, sm: 210 },
                      }
                    : {
                        // Centred, and narrowed from 40% per the client's
                        // "narrowing the opacity slider would work fine".
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: { xs: "70%", sm: "32%" },
                      }),
                }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    width: "100%",
                    filter:
                      openPopup === true
                        ? "grayscale(100) opacity(1) brightness(0.5)"
                        : "none",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    paddingBlock: "10px",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ width: "95%", color: "#413A36", fontSize: "11px" }}
                  >
                    Historical Overlay Opacity
                  </Typography>
                  <Slider
                    className="slider__time"
                    aria-label="Historical overlay opacity"
                    value={overlayOpacity}
                    min={0}
                    max={1}
                    step={0.01}
                    onChange={handleOverlayOpacityChange}
                    valueLabelDisplay="auto"
                    sx={{ width: "95%" }}
                  />
                </Paper>
              </Box>
            )}
          </Grid>

          {showContribution && (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              sx={{
                backgroundColor: "#f5f5f5",
                overflow: "visible",
                position: "relative",
              }}
            >
              <ErrorBoundary>
                <Box sx={{ width: "100%", height: "100%", zIndex: 1000, position: "relative" }}>
                  <ContributionComp
                    setShowContribution={setShowContribution}
                    selectedAddress={selectedAddress}
                    setSelectMode={setSelectMode}
                    setSelectedAddress={setSelectedAddress}
                    storyVisibility={storyVisibility}
                    clearSelectedAddress={() => {
                      setSelectedAddress("");
                      setSelectedAddressLatLng(null);
                    }}
                    year={year}
                    setYear={setYear}
                    setSelectedMarker={setSelectedMarker}
                    selectedAddressLatLng={selectedAddressLatLng}
                    setSelectedAddressLatLng={setSelectedAddressLatLng}
                    locations={locations}
                    allowEmptyAddress={isCustomLocation || customSelectMode}
                    refreshData={fetchLocations}
                    addStoryMode={addStoryMode}
                    setAddStoryMode={setAddStoryMode}
                    activatePolygonDrawing={activatePolygonDrawing}
                    activateLineDrawing={activateLineDrawing}
                    polygonCoordinates={polygonCoordinates}
                    lineCoordinates={lineCoordinates}
                    customSelectMode={customSelectMode}
                    setCustomSelectMode={setCustomSelectMode}
                    selectionType={selectionType}
                    isExhibitionMode={isExhibitionMode}
                    onPolygonSubmitted={() => {
                      setPolygonCoordinates([]);
                      setDrawingPoints([]);
                      setIsDrawingPolygon(false);
                      setSelectionType(SHAPE_TYPE.MARKER);
                      setActiveTool("point");
                    }}
                    onLineSubmitted={() => {
                      setLineCoordinates([]);
                      setDrawingPoints([]);
                      setIsDrawingLine(false);
                      setSelectionType(SHAPE_TYPE.MARKER);
                      setActiveTool("point");
                    }}
                    onSubmissionSuccess={handleSubmissionSuccess}
                  />
                </Box>
              </ErrorBoundary>
            </Grid>
          )}
          {/* Wizard rendered as floating popup outside Grid - see below */}

          {isSidePanelOpen && (
            <Grid
              item
              xs={12}
              sm={isSidePanelFullscreen ? 12 : 6}
              md={isSidePanelFullscreen ? 12 : 4}
              sx={{
                backgroundColor: "#f5f5f5",
                overflowY: "auto",
                position: "relative",
                height: "100%",
              }}
            >
              <SidePanelComponent
                isSidePanelOpen={isSidePanelOpen}
                setIsSidePanelOpen={setIsSidePanelOpen}
                isSidePanelFullscreen={isSidePanelFullscreen}
                setIsSidePanelFullscreen={setIsSidePanelFullscreen}
                year={year}
                setYear={setYear}
                setSelectedMarker={setSelectedMarker}
                selectedAddressLatLng={selectedAddressLatLng}
                setSelectedAddressLatLng={setSelectedAddressLatLng}
                setShowContribution={setShowContribution}
                setSelectedAddress={setSelectedAddress}
                setStoryVisibility={setStoryVisibility}
                markerIds={selectedMarkerIds}
                refreshData={fetchLocations}
                setIsEditing={setIsEditing}
                isEditing={isEditing}
                addStoryMode={addStoryMode}
                setAddStoryMode={setAddStoryMode}
                polygonData={polygonData}
                lineData={lineData}
                selectedAddress={selectedAddress}
                locationData={locations}
                isExhibitionMode={isExhibitionMode}
                setSelectionType={setSelectionType}
                setPolygonCoordinates={setPolygonCoordinates}
                setLineCoordinates={setLineCoordinates}
                polygons={polygons}
                polylines={polylines}
                pendingFocusShapeId={pendingFocusShapeId}
                onPendingFocusConsumed={handlePendingFocusConsumed}
              />
            </Grid>
          )}
          </Grid>
        </Box>
      )}
      {showWizard && (
        <WizardContribution
          onClose={closeWizard}
          onSwitchToLegacy={switchWizardToLegacy}
          selectedAddress={selectedAddress}
          setSelectedAddress={setSelectedAddress}
          selectedAddressLatLng={selectedAddressLatLng}
          setSelectedAddressLatLng={setSelectedAddressLatLng}
          setSelectMode={setSelectMode}
          selectMode={selectMode}
          year={year}
          setYear={setYear}
          refreshData={fetchLocations}
          locations={locations}
          isExhibitionMode={isExhibitionMode}
          mapRef={mapRef}
          activatePolygonDrawing={activatePolygonDrawing}
          isDrawingPolygon={isDrawingPolygon}
          polygonCoordinates={polygonCoordinates}
          setPolygonCoordinates={setPolygonCoordinates}
          activateLineDrawing={activateLineDrawing}
          isDrawingLine={isDrawingLine}
          lineCoordinates={lineCoordinates}
          setLineCoordinates={setLineCoordinates}
          setIsDrawingLine={setIsDrawingLine}
          setSelectionType={setSelectionType}
          setIsDrawingPolygon={setIsDrawingPolygon}
          setDrawingPoints={setDrawingPoints}
          setActiveTool={setActiveTool}
          customSelectMode={customSelectMode}
          setCustomSelectMode={setCustomSelectMode}
          onSubmissionSuccess={handleSubmissionSuccess}
        />
      )}
      <Toastr
        open={joinRequestToast.open}
        message={joinRequestToast.message}
        severity={joinRequestToast.severity}
        onClose={(event, reason) => {
          if (reason === "clickaway") return;
          setJoinRequestToast((prev) => ({ ...prev, open: false }));
        }}
      />
      <Toastr
        open={profileToast.open}
        message={profileToast.message}
        severity={profileToast.severity}
        autoHideDuration={4000}
        onClose={(event, reason) => {
          if (reason === "clickaway") return;
          setProfileToast((prev) => ({ ...prev, open: false }));
        }}
      />
      {/* Post-signup wizard. Mounts only for users authContext flagged as
          new (isNewUser === true). The wizard itself flips that flag back
          to false on Finish/Skip via markOnboardingComplete(). We gate on
          isExhibitionMode so kiosks never see a profile editor. */}
      {user && !isExhibitionMode && isNewUser && (
        <OnboardingWizard
          open
          onClose={() => {
            // Defensive — markOnboardingComplete should have already
            // fired inside the wizard, but if onClose somehow happens
            // first we still want to drop the flag.
            markOnboardingComplete();
          }}
          onComplete={({ skipped }) => {
            setProfileToast({
              open: true,
              severity: skipped ? "info" : "success",
              message: skipped
                ? "You can finish setting up your profile any time from the user menu."
                : "Welcome to Map250! Your profile is set up.",
            });
          }}
        />
      )}
    </Box>
  );
}

export default MapComponent;
